// src/App.jsx
import ErrorBoundary from './components/ErrorBoundary';
import { useState, useEffect, useRef, useCallback, Children, Fragment } from 'react';
import { AuthProvider, useAuth, deviceLabel } from './hooks/useAuth';
import { addPasskey, declineOffer, unlockWord } from './utils/passkeys';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import Usage from './pages/Usage';
import Users from './pages/Users';
import { logTabOpened, fetchBadges, markBoxSeen, fetchFeedbackCount } from './utils/api';
import ControlPanel from './pages/ControlPanel';
import SecurityPage from './pages/SecurityPage';   // 3.49.0: Super Admin pages out of the Console
import Analytics from './pages/Analytics';
import CostAnalysisPage from './pages/CostAnalysisPage';
import HealthPage from './pages/HealthPage';
import TwoFactorRequired from './pages/TwoFactorRequired';
import ResetTwoFactorLink, { resetTokenFromLocation } from './pages/ResetTwoFactorLink';
import RequestAccess, { accessTokenFromLocation } from './pages/RequestAccess';
import SetPassword, { setPasswordTokenFromLocation } from './pages/SetPassword';
import ResetPassword, { resetPasswordCodeFromLocation } from './pages/ResetPassword';
import ConfirmEmail, { confirmEmailTokenFromLocation } from './pages/ConfirmEmail';
import FaxEmails, { faxEmailsLinkFromLocation } from './pages/FaxEmails';
import SharedNote, { sharedNoteLinkFromLocation } from './pages/SharedNote';
import ChooseOwnPassword from './pages/ChooseOwnPassword';
import CompleteProfile from './pages/CompleteProfile';
import ProfileDialog from './components/ProfileDialog';
import SettingsDialog from './components/SettingsDialog';
import SessionsDialog from './components/SessionsDialog';
import HelpDialog from './components/HelpDialog';
import SideRail, { RAIL_WIDTH } from './components/SideRail';
import RightRail, { RIGHT_MENU_OPEN } from './components/RightRail';
import { trackPage } from './utils/visits';
import { watchWebApp } from './utils/appReport';
import useScrollReveal from './hooks/useScrollReveal';
import useSlidingPills from './hooks/useSlidingPills';
import SupportDialog from './components/SupportDialog';
import FeedbackDialog from './components/FeedbackDialog';
import NotesDialog from './components/NotesDialog';
import FailedAgainDialog from './components/FailedAgainDialog';
import { loadFaxReasons } from './utils/faxReasons';
import { PreferencesProvider, usePreferences } from './hooks/usePreferences';
import { useAppearance, useLoadingLook } from './theme/useAppearance';
import { effectiveTheme, effectiveMode } from './theme/themes';
import ThemeSwatches from './components/ThemeSwatches';
import ShortcutsDialog from './components/ShortcutsDialog';
import { useShortcuts } from './hooks/useShortcuts';
import { useAutoLabels } from './hooks/useAutoLabels';
import AppearanceSwitch from './components/AppearanceSwitch';
import { groupLabelFor, roleLabelFor, isSuperAdmin } from './utils/account';
import { useFaxNotices } from './hooks/useFaxNotices';
import { requestOpenFax } from './utils/openFaxRequest';
import { NotifyProvider, useNotify } from './components/Notifications';
import WhatsNew, { CURRENT_VERSION, RELEASES } from './components/WhatsNew';
import Footer from './components/Footer';
import UpdateBar from './components/UpdateBar';
import { leavePage, setUpdateTab } from './utils/appUpdate';
import { LOGO_URL, LOGO_FAX_PORTAL_URL } from './utils/brand';
import Loader from './components/Loader';
import Ambient from './components/Ambient';
import { TABS, DEFAULT_TAB, pathForTab, tabFromLocation, canOpenTab as tabAllowed, firstOpenTab, labelForTab, START_TABS } from './utils/tabRoutes';
import PurgedFaxes from './pages/PurgedFaxes';
import FaxContacts from './pages/FaxContacts';
import Settings from './pages/Settings';
import FailedLogs from './pages/FailedLogs';
import ViewAsDialog from './components/ViewAsDialog';
import ClientSwitcher from './components/ClientSwitcher';
import SessionCountdown from './components/SessionCountdown';
import HeaderClock from './components/HeaderClock';
import AppSignIn from './pages/AppSignIn';
import { pendingAppSignIn } from './utils/appHandoff';
import Avatar from './components/Avatar';
import AvatarDialog from './components/AvatarDialog';
import UnregisteredAlert from './components/UnregisteredAlert';
import { API_BASE } from './firebase/config';
import PhiBadge from './components/PhiBadge';
import Home from './pages/Home';
import MainNav from './components/MainNav';
import useWindowWidth, { usePhone, PHONE_MEDIA } from './hooks/useWindowWidth';
import BottomBar from './components/BottomBar';
import GuidedTour, { buildTourSteps, TOUR_VERSION } from './components/GuidedTour';
import NotificationBell from './components/NotificationBell';
import InstallGuide from './components/InstallGuide';
import useInstallApp from './hooks/useInstallApp';
import Compose from './pages/Compose';
import Emo, { emoLabel } from './components/Emo';
import Compliance from './pages/Compliance';
import Documents from './pages/Documents';
import Apps from './pages/Apps';
import ReportBug from './components/ReportBug';

// Per-browser memory of which version's notes this person has seen.
// Storage can be unavailable (private mode, blocked site data), so every
// access is guarded and the portal works the same without it.
const SEEN_KEY   = 'faxPortal.whatsNewSeen';
const NOTICE_KEY = 'faxPortal.updateNoticeShown';
function readStore(key) { try { return window.localStorage.getItem(key); } catch { return null; } }
function writeStore(key, value) { try { window.localStorage.setItem(key, value); } catch { /* ignore */ } }

const PREFS_WAIT_MS = 2500;

function AppContent() {
  const { user, userInfo, loading, logout, getToken, needsTwoFactor, checkingTwoFactor, twoFactorOffline, refreshUser, needsPasswordChange, signingIn,
          viewAs, viewAsReady, exitViewAs, effectiveInfo, can, accountVersion, passkeyOffer, endPasskeyOffer } = useAuth();
  const [showViewAs, setShowViewAs] = useState(false);
  // Phones (3.20.1): the header stacks and the page uses the full width
  const phone = usePhone();
  // 3.51.0: tablets (iPad portrait) and small computer windows: a compact menu bar
  const winWidth = useWindowWidth();
  const compactBar = !phone && winWidth < 1100;
  // 3.51.0: phones: the More sheet of the bottom bar
  const [moreOpen, setMoreOpen] = useState(false);
  // 3.52.0: the installable app: Install the app (Help), the iPhone guide
  const installApp = useInstallApp();
  const [showInstall, setShowInstall] = useState(false);
  const startInstall = async () => { if ((await installApp.install()) === 'ios') setShowInstall(true); };
  // 3.39.0: the top bar (header + menu) stays fixed; scrolled down it turns
  // see-through (3.56.7: one height); the icon strip sits under it (its height)
  const [scrolled, setScrolled] = useState(false);
  const [contentEl, setContentEl] = useState(null);   // the page area (3.39.0: scroll reveal)
  const [topH, setTopH] = useState(0);
  const [fullH, setFullH] = useState(0);   // the bar's height at full size: the space kept for it
  const topRef = useRef(null), navRowRef = useRef(null), userColRef = useRef(null), headerRef = useRef(null);
  // 3.49.13: the side menus start right under the header (beside the menu bar)
  const [headH, setHeadH] = useState(0);
  // 3.56.7 (owner's recording): the top bar flickered between full and slim near
  // the top of every page. While it grew or shrank, the space kept for it was
  // re-measured mid-animation; the browser then moved the page to keep your place
  // (scroll anchoring), which pushed the scroll position back across the switch
  // point, over and over. Now that space is measured only when the full bar has
  // settled (never mid-animation or while slim), so nothing under it moves.
  const scrolledRef = useRef(false), settleRef = useRef(0);
  useEffect(() => {
    // 3.39.1: slim after 80 px, full again only near the top (no bouncing)
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrolled(was => (was ? window.scrollY > 20 : window.scrollY > 80));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);
  const measureFull = useCallback(() => {
    const el = topRef.current;
    if (!el || scrolledRef.current || Date.now() < settleRef.current) return;
    const h = Math.round(el.getBoundingClientRect().height);
    setFullH(was => (Math.abs(was - h) > 1 ? h : was));
  }, []);
  useEffect(() => {
    scrolledRef.current = scrolled;
    settleRef.current = Date.now() + 450;   // the bar's size change takes about .2–.3 s
    if (scrolled) return undefined;
    const t = setTimeout(measureFull, 470);   // full again: measure once it has settled
    return () => clearTimeout(t);
  }, [scrolled, measureFull]);
  useEffect(() => {
    const el = topRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      setTopH(el.getBoundingClientRect().height);
      measureFull();
      if (headerRef.current) setHeadH(headerRef.current.getBoundingClientRect().height);
    });
    ro.observe(el);
    if (headerRef.current) ro.observe(headerRef.current);
    return () => ro.disconnect();
  });
  // Keeps the 2FA setup screen open for its last step (saving recovery
  // codes) after the authenticator itself is saved
  const [holdTwoFactorSetup, setHoldTwoFactorSetup] = useState(false);
  const { notify, confirm } = useNotify();
  // The open tab follows the address bar (see utils/tabRoutes.js)
  const [activeTab, setActiveTabState]    = useState(tabFromLocation);
  useScrollReveal(contentEl, activeTab);   // 3.39.0
  useSlidingPills();   // 3.39.2
  const [showUserMenu, setShowUserMenu]   = useState(false);
  // 3.36.4: the menu under your name slides in from the right and slides
  // out when the mouse leaves it (after a moment), on its arrow, on a click
  // outside or on Escape.
  // 3.48.7: with a mouse it opens by pointing at your name card, not by
  // clicking. Clicking the card just keeps it open; the keyboard (Enter) and
  // touch screens still open and close it with a press
  const [menuOpens, setMenuOpens] = useState(0);   // replays the items' slide-in each time
  const userBtnRef = useRef(null), menuCloseRef = useRef(null), menuLeaveTimer = useRef(null);
  const menuEnterTimer = useRef(null), menuOpenRef = useRef(false), menuBy = useRef('mouse'), lastPointer = useRef('mouse');
  const userMenuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 80, right: 16, width: 260 });   // computers: the card sits under your name
  menuOpenRef.current = showUserMenu;
  const canHover = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
  function openUserMenu(by) {
    menuBy.current = by;
    clearTimeout(menuLeaveTimer.current); clearTimeout(menuEnterTimer.current);
    if (menuOpenRef.current) return;
    menuOpenRef.current = true;
    const r = userBtnRef.current?.getBoundingClientRect();
    // 3.48.9: as wide as your name card. 3.63.0 (owner): on computers its right
    // edge stops before the right strip, so it never covers "You" at the strip's
    // top (pointing there used to land on this menu and the strip didn't open)
    if (r) {
      const edge = Math.round(window.innerWidth - r.right);
      const right = phone ? Math.max(12, edge) : Math.max(12, edge, RAIL_WIDTH + 8);
      setMenuPos({ top: Math.round(r.bottom + 8), right, width: Math.max(200, Math.round(r.width) - (right - edge)) });
    }
    setMenuOpens(n => n + 1);
    setShowUserMenu(true);
  }
  function toggleUserMenu(by) {
    if (menuOpenRef.current) setShowUserMenu(false); else openUserMenu(by);
  }
  // Pointing: open after a short pause, so passing by doesn't open it
  function pointToOpen(wait) {
    if (!canHover) return;
    clearTimeout(menuLeaveTimer.current); clearTimeout(menuEnterTimer.current);
    menuEnterTimer.current = setTimeout(() => openUserMenu('mouse'), wait);
  }
  function pointAway() {
    clearTimeout(menuEnterTimer.current);
    if (!menuOpenRef.current) return;
    clearTimeout(menuLeaveTimer.current);
    menuLeaveTimer.current = setTimeout(() => setShowUserMenu(false), 500);
  }
  useEffect(() => () => { clearTimeout(menuEnterTimer.current); clearTimeout(menuLeaveTimer.current); }, []);
  // Computers: a click anywhere outside the card (or your name) closes it
  useEffect(() => {
    if (!showUserMenu || phone) return;
    const onDown = e => { if (!userMenuRef.current?.contains(e.target) && !userBtnRef.current?.contains(e.target) && !e.target.closest?.('[data-tour-ui]')) setShowUserMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showUserMenu, phone]);
  useEffect(() => {
    clearTimeout(menuLeaveTimer.current);
    if (!showUserMenu) return;
    // the keyboard moves into the menu; the mouse leaves focus where it is
    const t = menuBy.current === 'keyboard' ? setTimeout(() => menuCloseRef.current?.focus({ preventScroll: true }), 50) : null;
    const onKey = e => { if (e.key === 'Escape') { setShowUserMenu(false); userBtnRef.current?.focus(); } };
    document.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey); };
  }, [showUserMenu]);
  const { prefs, loadedFor, setPreference } = usePreferences();
  const [prefsWaitOver, setPrefsWaitOver] = useState(false);
  const [showProfile, setShowProfile]     = useState(false);
  const [showSettings, setShowSettings]   = useState(false);
  const [showSessions, setShowSessions]   = useState(false);   // My Sessions pop-up (3.29.3)
  const [showHelp, setShowHelp]           = useState(false);   // Help pop-up (3.29.3)
  const [showShortcuts, setShowShortcuts] = useState(false);   // the list of keyboard shortcuts (3.47.0)
  // 3.37.0: Support, Feedback ('send' | 'all') and My notes
  const [showSupport, setShowSupport]     = useState(false);
  const [showFeedback, setShowFeedback]   = useState(false);
  const [showBug, setShowBug]             = useState(false);   // 3.83.0
  const [showNotes, setShowNotes]         = useState(false);   // true, or a note's id to open (3.74.0: a reminder)
  // 3.74.0: a My notes reminder (bell, or a phone notification's ?notes=1) opens My notes
  useEffect(() => {
    const onOpen = e => setShowNotes(e.detail?.noteId || true);
    window.addEventListener('open-notes', onOpen);
    try {
      const u = new URL(window.location.href);
      // 3.76.0: ?notes=<id> (a reminder email) opens that note
      if (u.searchParams.has('notes')) { const v = u.searchParams.get('notes'); setShowNotes(v && v !== '1' ? v.slice(0, 60) : true); u.searchParams.delete('notes'); window.history.replaceState(null, '', u.pathname + u.search + u.hash); }
    } catch { /* fine */ }
    return () => window.removeEventListener('open-notes', onOpen);
  }, []);
  const [failedAgain, setFailedAgain]     = useState(null);   // 3.57.7: the "failed again" window
  const [feedbackNew, setFeedbackNew]     = useState(0);       // Super Admins: feedback not looked at yet
  // 3.22.2: the clock's Time zone pop-up, and Profile picture
  const [showTimeZone, setShowTimeZone]   = useState(false);
  const [showAvatar, setShowAvatar]       = useState(false);
  const [showWhatsNew, setShowWhatsNew]   = useState(false);
  const [seenVersion, setSeenVersion]     = useState(() => readStore(SEEN_KEY));
  const [appSignIn, setAppSignIn]         = useState(() => pendingAppSignIn());   // 3.82.0
  const signInShown = useRef(false);   // they signed in here, so the app's sign-in needs no extra tap
  const hasUnseenUpdate = seenVersion !== CURRENT_VERSION;

  function openWhatsNew() {
    setShowWhatsNew(true);
    setShowUserMenu(false);
    writeStore(SEEN_KEY, CURRENT_VERSION);
    setSeenVersion(CURRENT_VERSION);
  }

  // After an update, tell each person once (per browser). 3.37.0: just the
  // notice, no link to the notes (the version button and What's new in the
  // menu still open them)
  useEffect(() => {
    if (!user || !hasUnseenUpdate || readStore(NOTICE_KEY) === CURRENT_VERSION) return;
    writeStore(NOTICE_KEY, CURRENT_VERSION);
    notify({
      tone: 'info',
      title: `The Fax Portal was updated to version ${CURRENT_VERSION}`,
      detail: RELEASES[0].title,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Tabs follow the role's permissions (Roles & Access). While a Super
  // Admin uses View as, everything follows the viewed person's role.
  const isAdmin     = !!user && isSuperAdmin(effectiveInfo, user.email);
  // Individual clients (3.19.12): the Users tab is called Staff
  const staffWording = (effectiveInfo?.accounts || []).find(a => a.id === effectiveInfo?.accountId)?.type === 'individual';
  const unassignedNumbers = effectiveInfo?.unassignedNumbers || 0;
  // 3.22.1, Gmail-style: how many faxes arrived since they last opened
  // Inbox, and a dot on Outbox when one of their faxes failed since they last
  // opened it. Opening the list (while the window is showing) marks it seen.
  const [badges, setBadges] = useState({ inbox: 0, outboxFailed: false });
  const [badgeNudge, setBadgeNudge] = useState(0);   // a fax pop-up arrived: count again now
  const realSuper   = !!user && isSuperAdmin(userInfo, user.email);

  // Feedback (3.37.0): Super Admins see how many came in that nobody has
  // looked at (beside Feedback in the menu); the feedback email's button
  // (?feedback=all) opens the list
  const loadFeedbackCount = useCallback(() => {
    if (realSuper) fetchFeedbackCount(getToken).then(j => setFeedbackNew(j.new || 0)).catch(() => {});
  }, [realSuper, getToken]);
  useEffect(() => { loadFeedbackCount(); }, [loadFeedbackCount, showUserMenu]);
  useEffect(() => {
    if (!realSuper || new URLSearchParams(window.location.search).get('feedback') !== 'all') return;
    setShowFeedback('all');
    const u = new URL(window.location.href); u.searchParams.delete('feedback');
    window.history.replaceState(window.history.state, '', u.pathname + u.search);
  }, [realSuper]);
  const tabAccess   = { isSuperAdmin: isAdmin, can, flags: { complianceReport: !!effectiveInfo?.complianceReport }, level: effectiveInfo?.level || '' };
  const canOpenTab  = id => tabAllowed(id, tabAccess);
  // 3.52.0: on phones, once: a tip to install the app (it stays in Help)
  const canInstallApp = installApp.canInstall;
  useEffect(() => {
    if (!user || !viewAsReady || !phone || !canInstallApp) return undefined;
    try { if (localStorage.getItem('faxPortal.installTip')) return undefined; } catch { return undefined; }
    const t = setTimeout(() => {
      try { localStorage.setItem('faxPortal.installTip', new Date().toISOString()); } catch { /* shown once anyway */ }
      notify({ tone: 'info', icon: '📲', title: 'Get the Fax Portal app', detail: 'Put CP Fax Portal on your Home Screen: one tap to open, full screen. It\'s also in your menu under Help.', hint: 'Install ›', onClick: startInstall });
    }, 4000);
    return () => clearTimeout(t);
  }, [user, viewAsReady, phone, canInstallApp]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 3.53.0: the guided tour (components/GuidedTour). Everyone once: new
  // people on their first sign-in, everyone else on their next one; Finish or
  // Skip saves preferences.tourDone to their account. Super Admins don't get
  // it by themselves, and never in View as; Help → Take the tour plays it.
  const [tourSteps, setTourSteps] = useState(null);
  const [tourRail, setTourRail]   = useState(null);   // 'left' | 'right': keeps that strip open for the tour
  function startTour() {
    setShowUserMenu(false); setMoreOpen(false);
    const acct = (effectiveInfo?.accounts || []).find(a => a.id === effectiveInfo?.accountId);
    setTourSteps(buildTourSteps({
      phone, firstName: String(userInfo?.name || '').trim().split(/\s+/)[0] || '',
      can: id => canOpenTab(id), perm: p => can(p), individual: acct?.type === 'individual', training: prefs.showTraining !== false,
      hasSwitcher: !!document.querySelector('[data-tour="client-switcher"]'), superAdmin: isAdmin,
      // the ref too: one step closes the menu and the next opens it in the same moment
      openMenu: () => openUserMenu('tour'), closeMenu: () => { menuOpenRef.current = false; setShowUserMenu(false); },
      setRail: setTourRail, setMore: setMoreOpen,
    }));
  }
  function endTour() {
    setTourSteps(null); setTourRail(null); setShowUserMenu(false); setMoreOpen(false);
    try { sessionStorage.setItem('faxPortal.tourShown', '1'); } catch { /* saved to the account anyway */ }
    if (!viewAs) setPreference('tourDone', TOUR_VERSION);
  }
  const tourDue = !!user && viewAsReady && loadedFor === user.email && !realSuper && !viewAs && !tourSteps
    && !needsPasswordChange && !needsTwoFactor && !checkingTwoFactor && !holdTwoFactorSetup && !userInfo?.askProfile && (Number(prefs.tourDone) || 0) < TOUR_VERSION;
  useEffect(() => {
    if (!tourDue) return undefined;
    try { if (sessionStorage.getItem('faxPortal.tourShown')) return undefined; } catch { /* go on */ }
    let tries = 0;
    let t = null;
    // a moment after the portal opens, and not on top of another window
    const later = () => { t = setTimeout(() => {
      if (document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]') && ++tries < 20) { later(); return; }
      startTour();
    }, tries ? 3000 : 1500); };
    later();
    return () => clearTimeout(t);
  }, [tourDue]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 3.51.0: phones: the open section's button is scrolled into view in rows you swipe sideways
  useEffect(() => {
    if (!phone) return undefined;
    const show = () => document.querySelectorAll('main [role="tablist"]').forEach(l => {
      const on = l.querySelector('[aria-selected="true"]');
      if (on && l.scrollWidth > l.clientWidth) l.scrollLeft = on.offsetLeft - l.offsetLeft - (l.clientWidth - on.offsetWidth) / 2;
    });
    const t = setTimeout(show, 120);
    window.addEventListener('popstate', show);
    return () => { clearTimeout(t); window.removeEventListener('popstate', show); };
  }, [phone, activeTab]);

  // Fax pop-ups on every page (3.24.0): results of faxes this person can
  // see in Outbox / Inbox, unless they turned pop-ups off in Settings.
  // Clicking one opens Outbox / Inbox at that fax.
  useFaxNotices({
    enabled: !!user && viewAsReady,
    popups: prefs.faxNotices !== false,
    getToken, notify,
    showClient: isAdmin,
    // refresh the menu counts now (3.26.0), and tell the open page (3.36.5:
    // Outbox, Inbox and Overview update by themselves)
    onActivity: activity => {
      setBadgeNudge(n => n + 1);
      window.dispatchEvent(new CustomEvent('fax-activity', { detail: activity }));
    },
    // Their own pop-up kinds (3.30.1, Preferences); the role's are applied by the server
    allow: ev => { const k = { ...{ delivered: true, failed: true, received: true, incomingFailed: false }, ...(prefs.popupKinds || {}) };
      return k[ev.kind === 'autoRetrySent' ? 'failed' : ev.kind] !== false; },
    onOpen: (tab, faxId) => {
      if (!canOpenTab(tab)) return;
      requestOpenFax(tab, faxId);
      goToTab(tab);
    },
    onFailedAgain: ev => setFailedAgain(ev),
  });
  // 3.57.7: failure reasons in plain words (the same text as the emails)
  useEffect(() => { if (user) loadFaxReasons(getToken); }, [user, getToken]);

  // 3.52.1: a push notification opens /portal/inbox?fax=… or /outbox?fax=…:
  // that fax's details open once the list has it; the address is tidied
  useEffect(() => {
    if (!user || !viewAsReady) return;
    const u = new URL(window.location.href);
    const faxId = u.searchParams.get('fax');
    if (!faxId) return;
    u.searchParams.delete('fax'); u.searchParams.delete('source');
    window.history.replaceState(window.history.state, '', u.pathname + u.search);
    if ((activeTab === 'incoming' || activeTab === 'outgoing') && canOpenTab(activeTab)) requestOpenFax(activeTab, faxId.slice(0, 120));
  }, [user, viewAsReady]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Open a tab: update the address (a new history entry, so Back works)
  // unless replace is set, e.g. when correcting an address.
  function goToTab(tab, { replace = false } = {}) {
    const path = pathForTab(tab);
    // 3.62.30: a new version waiting loads with the new page (utils/appUpdate.js)
    if (!replace && tab !== activeTab && leavePage(path)) return;
    setActiveTabState(tab);
    if (window.location.pathname !== path || window.location.search) {
      window.history[replace ? 'replaceState' : 'pushState']({ tab }, '', path);
    }
  }

  // 3.39.1: from the menu or the strip, clicking the page you're on takes
  // you back to its start (e.g. Library's front page after reading an article)
  function goFromMenu(tab) {
    if (tab === activeTab) window.dispatchEvent(new CustomEvent('tab-reselect', { detail: tab }));
    goToTab(tab);
  }

  useEffect(() => { setUpdateTab(activeTab); }, [activeTab]);

  // Browser Back / Forward move between tabs
  useEffect(() => {
    const onPop = () => setActiveTabState(tabFromLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Signing out (or being signed out for inactivity) always resets to
  // Outgoing Faxes, so the next person to sign in on this browser never
  // lands on the previous person's tab. Opening a shared link while signed
  // out still works: the address is kept until someone signs in.
  const prevUserRef = useRef(user);
  useEffect(() => {
    const wasSignedIn = !!prevUserRef.current;
    prevUserRef.current = user;
    if (wasSignedIn && !user) {
      setHoldTwoFactorSetup(false);
      setActiveTabState(DEFAULT_TAB);
      setShowUserMenu(false);
      setShowProfile(false);
      setShowSettings(false);
      setShowSessions(false);
      setShowHelp(false);
      setShowWhatsNew(false);
      setShowSupport(false);
      setShowFeedback(false);
      setShowNotes(false);
      window.history.replaceState({ tab: DEFAULT_TAB }, '', pathForTab(DEFAULT_TAB));
    }
  }, [user]);

  // Once signed in: admin-only tabs fall back to Outgoing for other users,
  // and unknown addresses are tidied to the tab actually shown
  useEffect(() => {
    if (!user || new URLSearchParams(window.location.search).get('download')) return;
    if (!viewAsReady) return;
    if (!canOpenTab(activeTab)) { goToTab(firstOpenTab(tabAccess), { replace: true }); return; }
    const path = pathForTab(activeTab);
    const here = window.location.pathname;
    // Users and the Control Panel keep their section in the address
    // (…/users/activity, …/control-panel/billing); older addresses are
    // tidied by their SectionTabs
    const subPage = ['control', 'users', 'security', 'costs'].includes(activeTab) && here.startsWith(path + '/');
    if (here !== path && !subPage) window.history.replaceState({ tab: activeTab }, '', path);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, activeTab, viewAsReady, effectiveInfo]);

  // Give up waiting for settings after a short while (server slow/unreachable)
  useEffect(() => {
    if (!user) { setPrefsWaitOver(false); return; }
    const t = setTimeout(() => setPrefsWaitOver(true), PREFS_WAIT_MS);
    return () => clearTimeout(t);
  }, [user]);

  // "Open the portal on": once per sign-in, when opened at the portal's
  // base address (not a specific tab, not an email download link)
  const startTabApplied = useRef(false);
  useEffect(() => {
    if (!user) { startTabApplied.current = false; return; }
    if (startTabApplied.current || loadedFor !== user.email) return;
    startTabApplied.current = true;
    const atBase = window.location.pathname === pathForTab(DEFAULT_TAB) && !window.location.search;
    // Start tab (Preferences): the Overview by default (3.20.0); Compose,
    // Outbox or Inbox if chosen and their role opens it (3.30.3)
    const start = START_TABS.find(t => t.id === prefs.startTab)?.tab;
    if (atBase && start && canOpenTab(start) && activeTab === DEFAULT_TAB) goToTab(start, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loadedFor]);

  // Activity log: which tab was opened (Users and the Control Panel log
  // their sections themselves)
  useEffect(() => {
    if (user && viewAsReady && !['users', 'control', 'security', 'costs'].includes(activeTab)) { logTabOpened(activeTab, getToken); trackPage(activeTab, getToken); }   // 3.49.0: Analytics too
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeTab, viewAsReady]);

  // 3.83.4: the installed Web App reports each time it's opened (Analytics → Apps)
  useEffect(() => (user && viewAsReady ? watchWebApp(getToken) : undefined), [user, viewAsReady, getToken]);

  // Badges (3.22.1): every minute, when the window shows again, and on each
  // tab change; the open Inbox or Outbox is marked seen
  useEffect(() => {
    if (!user || needsTwoFactor || checkingTwoFactor || needsPasswordChange) return undefined;
    let live = true;
    const box = activeTab === 'incoming' ? 'inbox' : activeTab === 'outgoing' ? 'outbox' : null;
    const tick = async () => {
      try {
        if (box && !document.hidden) await markBoxSeen(box, getToken);
        const b = await fetchBadges(getToken);
        if (live && b?.success) setBadges({ inbox: b.inbox || 0, outboxFailed: !!b.outboxFailed });
      } catch { /* badges are optional */ }
    };
    tick();
    const timer = setInterval(tick, 60 * 1000);
    const onShow = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { live = false; clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [user, needsTwoFactor, checkingTwoFactor, needsPasswordChange, activeTab, getToken, accountVersion, badgeNudge]);

  // Browser tab title names the open tab (and, like Gmail, the Inbox count)
  useEffect(() => {
    document.title = user ? `${badges.inbox ? `(${badges.inbox}) ` : ''}${labelForTab(activeTab)} · Fax Portal` : 'Fax Portal';
  }, [user, activeTab, badges.inbox]);

  // Handle ?download=incoming/outgoing&faxId=xxx from email links
  useEffect(() => {
    if (!user) return;
    const params   = new URLSearchParams(window.location.search);
    const dlType   = params.get('download');
    const dlFaxId  = params.get('faxId');
    if (dlType && dlFaxId) {
      // Switch to correct tab
      const dlTab = dlType === 'incoming' ? 'incoming' : 'outgoing';
      setActiveTabState(dlTab);
      // Trigger download
      getToken().then(token => {
        fetch(`${API_BASE}/portal/download/${dlType}/${dlFaxId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
          if (!res.ok) throw new Error('Download failed');
          return res.blob();
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const a   = document.createElement('a');
          a.href    = url;
          a.download = `fax-${dlFaxId}.pdf`;
          a.click();
          URL.revokeObjectURL(url);
          // Clean URL without reload: drop the download parameters and
          // show the tab's own address
          window.history.replaceState({ tab: dlTab }, '', pathForTab(dlTab));
        })
        .catch(e => {
          notify({ tone: 'error', title: 'Download failed', detail: e.message });
          // Drop the download parameters so a refresh doesn't retry it
          window.history.replaceState({ tab: dlTab }, '', pathForTab(dlTab));
        });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 3.45.0: light or dark, only while the portal itself shows (never the sign-in pages)
  const askProfile = !!userInfo?.askProfile && !viewAs;
  const portalShown = !!user && !loading && viewAsReady && !needsPasswordChange && !needsTwoFactor && !checkingTwoFactor && !askProfile
    && (loadedFor === user.email || prefsWaitOver);
  // 3.46.0: and the colour theme: their own pick, else their client's starting theme, else Teal
  // 3.47.0: light or dark is likewise their own pick, else the client's starting mode, else Auto
  useAppearance(effectiveMode(prefs.appearance, userInfo?.clientMode), portalShown, effectiveTheme(prefs.theme, userInfo?.clientTheme));
  // 3.47.0: Gmail-style keys (/ N G… ?), unless turned off in Preferences
  useShortcuts({ enabled: portalShown && prefs.shortcuts !== false, goToTab, canOpenTab, onHelp: () => setShowShortcuts(true), notify });
  // 3.47.0: boxes without a name get the label just before them, for screen readers
  useAutoLabels(portalShown);
  // 3.69.0 (owner): right after a sign-in with the password (the app and,
  // since 3.69.1, computers): turn on Face ID / fingerprint unlock? (useAuth decides when;
  // not during the tour or View as). "Not now" asks again in 30 days.
  useEffect(() => {
    if (!passkeyOffer || !portalShown || tourSteps || viewAs) return undefined;
    const t = setTimeout(async () => {
      endPasskeyOffer();
      const word = unlockWord();
      const ok = await confirm({
        title: `Unlock with ${word} next time?`,
        message: `After an hour without use, the portal locks instead of signing you out. Open it again, or sign in after you sign out, with ${word}, without your portal password or code. Your portal password is still needed once every 30 days. Only do this on a device that's yours.`,
        confirmLabel: 'Turn it on', cancelLabel: 'Not now',
      });
      if (!ok) { declineOffer(user.email); return; }
      try {
        await addPasskey({ email: user.email, name: userInfo?.name || '', label: deviceLabel(), getToken: () => user.getIdToken() });
        notify({ tone: 'success', title: 'Unlock is on', detail: 'Next time the portal locks, open it without your password. You can turn it off in Security.' });
      } catch (e) {
        notify({ tone: e.code === 'CANCELLED' ? 'info' : 'error', title: 'Not turned on', detail: e.code === 'REAUTH_REQUIRED' ? 'You can turn it on in Security → Face ID & fingerprint.' : `${e.message} You can turn it on any time in Security.` });
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [passkeyOffer, portalShown, tourSteps, viewAs]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || (signingIn && !user)) return <LoadingScreen />;
  // Wait briefly for this person's settings (list size, start tab) so the
  // lists open the way they chose; never longer than PREFS_WAIT_MS.
  if (user && loadedFor !== user.email && !prefsWaitOver) return <LoadingScreen />;
  if (!user) { signInShown.current = true; return <Login />; }
  if (!viewAsReady) return <LoadingScreen />;
  // Signed in with a temporary password: choose their own first
  if (needsPasswordChange) return <ChooseOwnPassword />;
  // 3.79.1: no answer yet on their authenticator (a lost connection, say)
  if (checkingTwoFactor && !holdTwoFactorSetup) return <LoadingScreen offline={twoFactorOffline} />;
  // Two-factor authentication is required before the portal opens
  if (needsTwoFactor || holdTwoFactorSetup) {
    return <TwoFactorRequired
      onEnrolling={() => setHoldTwoFactorSetup(true)}
      onDone={async () => {
        // Re-read the account so the new authenticator is seen, then open the portal
        try { await refreshUser(); } catch { /* the hold is released anyway */ }
        setHoldTwoFactorSetup(false);
      }} />;
  }
  // 3.62.26 (owner): newly added people check their name, phone and extension once
  if (askProfile) return <CompleteProfile />;

  // 3.82.0: the CP Fax app signing in through this page, once they're fully signed in
  if (appSignIn) return <AppSignIn request={appSignIn} user={user} ask={!signInShown.current && !pendingAppSignIn()?.fresh} getToken={getToken} signOut={logout} onDone={() => setAppSignIn(null)} />;

  // The main menu (3.20.1: buttons; ☰ on phones)
  const mainNav = (
    <MainNav tabs={TABS.filter(t => canOpenTab(t.id))} active={activeTab} hrefFor={pathForTab} onGo={goFromMenu} compact={compactBar}
        alert={!!(badges.inbox || badges.outboxFailed || unassignedNumbers)}
        labelFor={t => (t.id === 'users' && staffWording ? 'Staff'
          : t.id === 'incoming' && badges.inbox ? <>{t.label}<span style={S.navCount} aria-label={`${badges.inbox} new`}>{badges.inbox > 99 ? '99+' : badges.inbox}</span></>
          : t.id === 'outgoing' && badges.outboxFailed ? <>{t.label}<span style={{ ...S.navDot, background: 'var(--c-d32f2f)' }} title="One of your faxes failed" aria-label="a fax failed" /></>
          // 3.21.1: faxes arrived on a number no client has (Control Panel → Clients)
          : t.id === 'control' && unassignedNumbers ? <>{t.label}<span style={S.navDot} title={`Faxes on ${unassignedNumbers} number${unassignedNumbers === 1 ? '' : 's'} no client has: see Clients`} aria-label="needs attention" /></>
          : t.label)} />
  );

  // 3.56.0: the notification bell (components/NotificationBell); not in View as
  const bell = viewAs ? null : (
    <NotificationBell getToken={getToken} nudge={badgeNudge} showClient={isAdmin} phone={phone}
      onOpen={(tab, faxId) => { if (!canOpenTab(tab)) return; requestOpenFax(tab, faxId); goToTab(tab); }} />
  );

  // 3.48.7: Your account and Help: the strip on the right (computers) and
  // the menu under your name (phones) list the same items
  const menuGroups = [
    // 3.49.19 (owner): My profile, My notes, Preferences, Security, My sessions
    { title: 'Your account', items: [
      { id: 'profile', icon: '👤', label: 'My profile', hint: 'Your picture, name, phone, role and client, secondary email, and your time zone', onClick: () => setShowProfile(true) },
      { id: 'notes', icon: '📝', label: 'My notes', hint: 'Your own notes and checklists. Only you can see them.', onClick: () => setShowNotes(true) },
      { id: 'prefs', icon: '🖥️', label: 'Preferences', hint: 'Theme and light or dark, keyboard shortcuts, which tab the portal opens on, list sizes, fax pop-ups and other choices', onClick: () => setShowSettings('portal') },
      // 3.29.3: "Settings" split in two, so it isn't confused with the Settings tab
      { id: 'security', icon: '🔒', label: 'Security', hint: 'Password, two-factor authentication (recovery codes, remembered devices) and Face ID / fingerprint unlock', onClick: () => setShowSettings('security') },
      ...((realSuper || userInfo?.perms?.['tab.mySessions']) ? [{ id: 'sessions', icon: '🔐', label: 'My sessions', hint: 'When you signed in and out of the portal', onClick: () => setShowSessions(true) }] : []),
      ...((realSuper || userInfo?.perms?.['users.viewAs']) && !viewAs ? [{ id: 'viewas', icon: '👁', label: 'View as…', hint: realSuper ? 'See and use the portal exactly as someone else does' : 'See the portal as a Manager or User at your company (read only)', onClick: () => setShowViewAs(true) }] : []),
      ...(viewAs ? [{ id: 'exitviewas', icon: '✕', label: 'Exit View as', hint: 'Go back to seeing the portal as yourself', onClick: exitViewAs, tone: 'warn' }] : []),
    ] },
    { title: 'Help', items: [
      // 3.52.0: shown where it can be installed and isn't yet
      ...(installApp.canInstall ? [{ id: 'install', icon: '📲', label: 'Install the app', hint: 'Put CP Fax Portal on your phone or computer as an app: its own icon, full screen, and it updates by itself', onClick: startInstall }] : []),
      // 3.40.0: "How do I…?" (was a second "Help" under the Help heading)
      { id: 'howto', icon: '❓', label: 'How do I…?', hint: 'Search for how to do something in the portal', onClick: () => setShowHelp(true) },
      { id: 'support', icon: '🛟', label: 'Support', hint: "Ask the Crazy Pixels team for help, or check on a request you've made", onClick: () => setShowSupport(true) },
      { id: 'feedback', icon: '💬', label: 'Feedback', hint: realSuper ? 'Send feedback, or see all the feedback people sent' : 'Tell us what works, what doesn’t, or what would make the portal better', onClick: () => setShowFeedback('send'), count: realSuper ? feedbackNew : 0 },
      // 3.49.19 (owner): Keyboard shortcuts just above What's new
      { id: 'shortcuts', icon: '⌨️', label: 'Keyboard shortcuts', hint: 'Single keys for search, new fax and moving between pages (press ? anywhere)', onClick: () => setShowShortcuts(true) },
      // 3.53.0 (owner): Take the tour, just above What's new
      { id: 'tour', icon: '🧭', label: 'Take the tour', hint: 'A one-minute tour of where everything is in the portal', onClick: startTour },
      { id: 'bug', icon: '🐞', label: 'Report a bug', hint: "Tell us about something that isn't working", onClick: () => setShowBug(true) },   // 3.83.0
      { id: 'whatsnew', icon: '✨', label: "What's new", hint: 'What changed in the portal, version by version', onClick: openWhatsNew, extra: `v${CURRENT_VERSION}` },
    ] },
  ];

  return (
    <div style={S.page}>
      {/* Header (3.39.0: the logo, title and version in the middle; the
          header and the main menu stay fixed at the top; scrolled down, the
          bar turns see-through; 3.56.7: it keeps one height) */}
      {/* 3.39.1: on computers the bar floats over the page (fixed) above a
          space of its full height, so slimming it never moves the page */}
      {/* 3.47.0: for keyboard and screen-reader users, straight past the header and menu */}
      <a href="#main-content" className="skip-link" onClick={e => { e.preventDefault(); document.getElementById('main-content')?.focus(); }}>Skip to the page</a>
      {!phone && <div style={{ height: fullH || topH }} aria-hidden="true" />}
      <div ref={topRef} style={{ ...S.stickyTop, ...(phone ? {} : S.fixedTop) }}>
      <header ref={headerRef} className={`app-header${scrolled && !phone ? ' is-scrolled' : ''}`} style={{ ...S.header, ...(phone ? S.headerPhone : S.headerGrid), ...(scrolled && !phone ? { ...S.headerScrolled, boxShadow: 'none' } : {}) }}>
        {/* 3.51.0: phones: one slim row: the client switcher, the logo and title, your picture */}
        {phone && <ClientSwitcher align="start" />}
        <div className="app-header-shine" aria-hidden="true" />
        {/* 3.47.3: on computers the theme dots and light · auto · dark sit here, at the left of
            the header, level with the logo (also in Preferences; both save to the account) */}
        {!phone && (
          <div style={S.headerLook} data-tour="theme">
            <ThemeSwatches compact id="head-theme" />
            <AppearanceSwitch compact id="head-appearance" />
          </div>
        )}
        <div style={{ ...S.headerLeft, ...(phone ? S.headerLeftPhone : { justifyContent: 'center' }) }}>
          {/* The logo and the title open the Dashboard (3.20.0) */}
          <a href={pathForTab('dashboard')} onClick={e => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); goToTab('dashboard'); }}
            title="Overview" style={{ display: 'flex' }}>
            {/* 3.51.3 (owner): phones and tablets: the "Crazy Pixels Fax Portal" logo (no title
                beside it); computers: the new "Crazy Pixels" logo with the Fax Portal title */}
            {phone || compactBar
              ? <img src={LOGO_FAX_PORTAL_URL} alt="Crazy Pixels Fax Portal: go to the Overview" className="brand-logo" style={{ ...S.headerLogo, height: phone ? 40 : 52 }} />
              : <img src={LOGO_URL} alt="Crazy Pixels: go to the Overview" className="brand-logo" style={{ ...S.headerLogo, height: 42 }} />}
          </a>
          <div>
            <div style={S.titleRow}>
              {!phone && !compactBar && <a href={pathForTab('dashboard')} onClick={e => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); goToTab('dashboard'); }}
                title="Overview" className="brand-title" style={{ ...S.portalTitle, fontSize: 23, textDecoration: 'none' }}>Fax Portal</a>}
              {/* 3.51.0: phones show the version in the menu (What's new) */}
              {!phone && <button onClick={openWhatsNew} style={S.versionChip}
                title="See what's new in this version"
                aria-label={`Version ${CURRENT_VERSION}. See what's new${hasUnseenUpdate ? ' (new updates)' : ''}`}>
                v{CURRENT_VERSION}
                {hasUnseenUpdate && <span style={S.newDot} aria-hidden="true" />}
              </button>}
              {/* 3.62.27 (owner): the HIPAA badge sits on the title line, after the
                  version, so the logo and title line up the same for every client
                  (it was on its own line under the title from 3.22.4) */}
              <PhiBadge compact={phone} />
            </div>
          </div>
        </div>
      <div style={{ ...S.headerRight, ...(phone ? S.headerRightPhone : { justifyContent: 'flex-end' }) }}>
          {/* 3.49.13: the same switcher on phones (3.51.0: at the left of the header) */}
          {/* User menu: picture, name, role and client; 3.22.4: the clock
              (its own Time zone pop-up) sits right under the card */}
          {/* 3.56.0: phones: the bell beside your picture */}
          {phone && bell}
          <div style={S.userCol} ref={userColRef}>
          <div style={{ position: 'relative' }}>
            <button ref={userBtnRef}
              onPointerDown={e => { lastPointer.current = e.pointerType || 'mouse'; }}
              onClick={e => (e.detail === 0 ? toggleUserMenu('keyboard') : canHover && lastPointer.current === 'mouse' ? openUserMenu('mouse') : toggleUserMenu('touch'))}
              onMouseEnter={() => pointToOpen(150)} onMouseLeave={pointAway}
              aria-haspopup="dialog" aria-expanded={showUserMenu} aria-controls="user-menu"
              className="user-card" data-tour="me" style={phone ? S.userBtnPhone : S.userBtn} aria-label={phone ? `Your menu: ${userInfo?.name || user.email}` : undefined}>
              <Avatar avatar={userInfo?.avatar} name={userInfo?.name || user.email} size={phone ? 34 : 30} />
              {/* 3.51.0: phones: just your picture (your name is in the menu it opens) */}
              {!phone && <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={S.userName}>{userInfo?.name || user.email}</span>
                <span style={S.userRole}>{roleLabelFor(user.email, userInfo)} · {groupLabelFor(user.email, userInfo)}</span>
              </div>
              {/* 3.48.8: « (not ▾): the card slides in from the right when you point at your name */}
              <span style={{ marginLeft: 8, color: 'var(--t-00897b)', fontSize: 17, fontWeight: 800, lineHeight: 1 }} aria-hidden="true">«</span>
              </>}
            </button>

          </div>
          {!phone && <HeaderClock onClick={() => setShowTimeZone(true)} />}
          </div>
        </div>
      </header>
      {viewAs && (
        <div style={S.viewAsBar} role="status">
          <span><Emo e="👁" /> Viewing as <strong>{viewAs.name}</strong> · {viewAs.roleLabel} · {viewAs.groupLabel}. {viewAs.readOnly
            ? <><strong>Read only:</strong> you see what they see, but can't change or send anything.</>
            : 'Anything you change or send is done as them and recorded in the Activity log.'}</span>
          <button onClick={exitViewAs} style={S.viewAsExit}>Exit View as</button>
        </div>
      )}
      {/* 3.39.0: computers: the menu sits in the fixed top bar; phones: as
          before, at the top of the page (the mobile pass will revisit it) */}
      {!phone && (
        <div ref={navRowRef} className={`app-nav-row${scrolled && !phone ? ' is-scrolled' : ''}`} style={{ ...S.navRow, '--rail-w': `${RAIL_WIDTH}px`, ...(compactBar ? { paddingLeft: RAIL_WIDTH + 10, paddingRight: RAIL_WIDTH + 10, gap: 8, gridTemplateColumns: 'minmax(0, 1fr) auto minmax(46px, 1fr)' } : {}) }}>
          {/* 3.49.13: the client switcher on the menu's row, centred in the
              space between the left side menu and the menu bar (owner's
              mock), so it grows from its middle to both sides; it was at
              the right, under the name card */}
          <div style={{ display: 'flex', justifyContent: 'center' }}><ClientSwitcher /></div>
          <div style={{ pointerEvents: 'auto' }}>{mainNav}</div>
          {/* 3.56.0 (owner): the notification bell, mirroring the client switcher on the other side.
              3.56.2 (owner): right beside the menu bar, so the right side menu sliding in and a
              left-strip page joining the menu bar no longer hide it */}
          <div style={{ display: 'flex', justifyContent: 'flex-start', pointerEvents: 'auto' }}>{bell}</div>
        </div>
      )}
      </div>

      {/* The menu under your name (3.36.4): slides in from the right; out
          when the mouse leaves it for a moment, on ›, a click outside or Escape.
          3.48.7: on computers it's a small card under your name (details,
          picture, Sign out) with no dimmed page behind it */}
      {phone && <div style={{ ...S.menuBackdrop, ...(showUserMenu ? S.menuBackdropOn : {}) }} onClick={() => setShowUserMenu(false)} aria-hidden="true" />}
      <div id="user-menu" ref={userMenuRef} role="dialog" aria-label="Your menu" aria-hidden={!showUserMenu} className="cp-ambient-host"
        style={phone ? { ...S.userMenu, ...(showUserMenu ? S.userMenuOn : {}) }
          : { ...S.userCard, top: menuPos.top, right: menuPos.right, width: menuPos.width, ...(showUserMenu ? S.userCardOn : { transform: `translateX(calc(100% + ${menuPos.right + 8}px))` }) }}
        onMouseEnter={() => clearTimeout(menuLeaveTimer.current)}
        onMouseLeave={pointAway}>
        <button ref={menuCloseRef} onClick={() => { setShowUserMenu(false); userBtnRef.current?.focus(); }} style={phone ? S.menuCloseBtn : { ...S.menuCloseBtn, ...S.cardCloseBtn }}
          title="Close the menu" aria-label="Close the menu">›</button>
        {/* 3.40.2: a soft moving background with drifting paper sheets */}
        <Ambient sheets={4} />
        {/* 3.48.9: on computers the card is as wide as your name card, so everything is a size smaller */}
        <div style={{ ...S.userMenuHeader, display: 'flex', alignItems: 'center', gap: phone ? 12 : 10, ...(phone ? {} : S.cardHeader) }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={phone ? S.userMenuName : { ...S.userMenuName, ...S.cardName }}>{userInfo?.name || user.email}</div>
            <div style={phone ? S.userMenuEmail : { ...S.userMenuEmail, ...S.cardLine }} title={user.email}>{user.email}</div>
            <div style={phone ? S.userMenuRole : { ...S.userMenuRole, ...S.cardLine }}>{roleLabelFor(user.email, userInfo)} · {phone ? 'Client: ' : ''}{groupLabelFor(user.email, userInfo)}</div>
            {/* 3.43.5: Sign Out right under your name, not at the very bottom */}
            <button onClick={logout} data-tour="signout" style={phone ? S.menuSignOut : { ...S.menuSignOut, ...S.cardSignOut }} className="menu-signout" title="Sign out of the Fax Portal on this browser">
              <Emo e="↩" /> Sign out
            </button>
          </div>
          {/* 3.22.4: the picture, with ✎ to change it */}
          <button onClick={() => { setShowAvatar(true); setShowUserMenu(false); }} style={S.menuAvatarBtn}
            title="Change your profile picture" aria-label="Change your profile picture">
            <Avatar avatar={userInfo?.avatar} name={userInfo?.name || user.email} size={phone ? 52 : 40} />
            <span style={phone ? S.menuAvatarEdit : { ...S.menuAvatarEdit, ...S.cardAvatarEdit }} aria-hidden="true">✎</span>
          </button>
        </div>
        <div key={menuOpens} className="user-menu-items">
          {/* 3.47.0: Theme, right under Sign out (3.47.2: one line, two ovals). 3.47.3: on
              computers it moved to the header; phones, whose header has no room, keep it here */}
          {phone && (
            <div style={S.menuThemeRow} data-tour="theme">
              <span style={S.menuThemeLabel}>Theme</span>
              <ThemeSwatches compact id="menu-theme" />
              <AppearanceSwitch compact id="menu-appearance" />
            </div>
          )}
          {/* 3.37.0, in this order: your account, your notes, (View as),
              help and contact (3.43.5: Sign Out moved up, under your name).
              3.48.7: on computers these moved to the strip on the right
              (RightRail); phones keep them here */}
          {phone && menuGroups.map(g => (
            <Fragment key={g.title}>
              <div style={S.menuSection}>{g.title}</div>
              {g.items.map(it => (
                <MenuItem key={it.id} onClick={() => { it.onClick(); setShowUserMenu(false); }} hint={it.hint} style={it.tone === 'warn' ? { color: 'var(--t-e65100)' } : undefined}>
                  {`${it.icon} ${it.label}`}
                  {it.count > 0 && <span style={S.menuCount} aria-label={`${it.count} new`}>{it.count}</span>}
                  {it.extra && <span style={S.menuVersion}>{it.extra}</span>}
                </MenuItem>
              ))}
            </Fragment>
          ))}
        </div>
      </div>

      {/* 3.39.0: the pages people open now and then, as an icon strip */}
      {/* 3.48.8: starts under the whole top bar, level with the right strip */}
      {!phone && <SideRail top={headH || topH} active={activeTab} hrefFor={pathForTab} onGo={goFromMenu}
        // 3.40.0: someone who can open Settings but not Users sees the group as just Settings
        tabs={TABS.filter(t => t.rail && canOpenTab(t.id)).map(t => (t.railGroup === 'People & settings' && !canOpenTab('users') ? { ...t, railGroup: 'Settings' } : t))}
        labelFor={t => (t.id === 'users' && staffWording ? 'Staff' : t.label)}
        alerts={unassignedNumbers ? { control: `faxes on ${unassignedNumbers} number${unassignedNumbers === 1 ? '' : 's'} no client has` } : {}} forceOpen={tourRail === 'left'} />}

      {/* 3.48.7: Your account and Help, as an icon strip on the right */}
      {!phone && <RightRail top={headH || topH} groups={menuGroups} forceOpen={tourRail === 'right'} />}   {/* 3.49.13: right under the header, beside the menu bar */}

      <main id="main-content" tabIndex={-1} style={{ ...S.main, ...(phone ? S.mainPhone : { paddingLeft: RAIL_WIDTH + 24, paddingRight: RAIL_WIDTH + 24 }) }}>
        {/* 3.51.0: phones use the bottom bar (components/BottomBar) instead of the ☰ menu */}
        {/* Content */}
        {/* Remounted when the account changes, so every tab reloads for it */}
        <div style={S.content} key={`account-${accountVersion}-${prefs.timeZone}`}>
        {/* 3.39.0: each page fades in when you switch to it */}
        <div key={activeTab} className="page-in" ref={setContentEl}>
          {/* 3.37.1: New faxes on the Overview is the Inbox tab's own count */}
          {activeTab === 'dashboard' && <Home inboxNew={badges.inbox} onInboxSeen={() => { setBadges(b => ({ ...b, inbox: 0 })); setBadgeNudge(n => n + 1); }} />}
          {activeTab === 'compose' && canOpenTab('compose') && <Compose onOpen={goToTab} />}
          {(activeTab === 'outgoing' || activeTab === 'incoming') && (
            <Dashboard activeTab={activeTab} />
          )}
          {activeTab === 'kb' && <KnowledgeBase />}
          {activeTab === 'usage' && <Usage />}
          {activeTab === 'compliance' && canOpenTab('compliance') && <Compliance />}   {/* 3.43.0 */}
          {activeTab === 'documents' && canOpenTab('documents') && <Documents />}   {/* 3.68.0 */}
          {activeTab === 'apps' && <Apps />}   {/* 3.82.1 */}
          {activeTab === 'purged' && canOpenTab('purged') && <PurgedFaxes />}
          {activeTab === 'contacts' && canOpenTab('contacts') && <FaxContacts />}
          {activeTab === 'settings' && canOpenTab('settings') && <Settings />}
          {activeTab === 'logs' && canOpenTab('logs') && <FailedLogs />}
          {activeTab === 'users' && canOpenTab('users') && <Users superAdmin={isAdmin} />}
          {activeTab === 'control' && canOpenTab('control') && <ControlPanel />}
          {activeTab === 'security' && canOpenTab('security') && <SecurityPage />}
          {activeTab === 'analytics' && canOpenTab('analytics') && <Analytics />}
          {activeTab === 'costs' && canOpenTab('costs') && <CostAnalysisPage />}
          {activeTab === 'health' && canOpenTab('health') && <HealthPage />}
        </div>
        </div>
      </main>

      <Footer />
      {/* 3.51.0: phones: the bar at the bottom, and room for it under the footer */}
      {phone && <div style={{ height: 'var(--cp-bottom-bar, 0px)', flexShrink: 0 }} aria-hidden="true" />}
      {phone && <BottomBar tabs={TABS.filter(t => canOpenTab(t.id))} active={activeTab} hrefFor={pathForTab} onGo={goFromMenu}
        labelFor={(t, short) => (t.id === 'users' && staffWording ? 'Staff' : short && t.id === 'dashboard' ? 'Overview' : t.label)}
        badges={badges} moreOpen={moreOpen} setMoreOpen={setMoreOpen}
        alerts={unassignedNumbers ? { control: `faxes on ${unassignedNumbers} number${unassignedNumbers === 1 ? '' : 's'} no client has` } : {}} />}

      {/* Inactivity: countdown for the last 5 minutes */}
      <SessionCountdown />

      {/* Change Password Modal */}
      {showProfile  && <ProfileDialog focus={showProfile === 'timeZone' ? 'timeZone' : null} onClose={() => setShowProfile(false)}
        onPicture={() => { setShowProfile(false); setShowAvatar(true); }} />}
      {showTimeZone && <ProfileDialog only="timeZone" onClose={() => setShowTimeZone(false)} />}
      {showAvatar   && <AvatarDialog onClose={() => setShowAvatar(false)} />}
      {showSettings && <SettingsDialog part={showSettings === true ? null : String(showSettings).split(':')[0]} tab={String(showSettings).split(':')[1]}
        onClose={() => setShowSettings(false)} onShortcuts={() => { setShowSettings(false); setShowShortcuts(true); }} />}   {/* 'portal:general' opens a tab (3.73.0) */}
      {showShortcuts && <ShortcutsDialog onClose={() => setShowShortcuts(false)} onPreferences={() => { setShowShortcuts(false); setShowSettings('portal:general'); }} />}
      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} onOpenLibrary={id => { setShowHelp(false); if (canOpenTab('kb')) { if (id) { try { sessionStorage.setItem('kb.open', id); } catch { /* ignore */ } window.dispatchEvent(new CustomEvent('kb-open', { detail: id })); } goToTab('kb'); } }} />}
      {showSessions && <SessionsDialog onClose={() => setShowSessions(false)} />}
      {showSupport && <SupportDialog onClose={() => setShowSupport(false)} />}
      {showBug && <ReportBug page={labelForTab(activeTab) || ''} onClose={() => setShowBug(false)} />}
      {showFeedback && <FeedbackDialog onClose={() => { setShowFeedback(false); loadFeedbackCount(); }} getToken={getToken} notify={notify} confirm={confirm}
        activeTab={activeTab} superAdmin={realSuper} startOn={showFeedback} onCountChange={loadFeedbackCount} />}
      {showNotes && <NotesDialog key={String(showNotes)} openNoteId={showNotes === true ? null : showNotes} onClose={() => setShowNotes(false)} getToken={getToken} notify={notify} confirm={confirm} />}
      {failedAgain && !viewAs && <FailedAgainDialog ev={failedAgain} onClose={() => setFailedAgain(null)}
        onOpenFax={ev => { if (canOpenTab('outgoing')) { requestOpenFax('outgoing', ev.faxId); goToTab('outgoing'); } }} />}
      {showViewAs && <ViewAsDialog onClose={() => setShowViewAs(false)} />}
      <UnregisteredAlert onOpenList={() => {
        try { window.sessionStorage.setItem('faxPortal.userMgmtView', 'senders'); } catch { /* ignore */ }
        const path = `${pathForTab('users')}/people`;
        setActiveTabState('users');
        window.history.pushState({ tab: 'users', sub: 'people' }, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }} />

      {/* 3.52.0: iPhone and iPad: how to add the app to the Home Screen */}
      {showInstall && <InstallGuide onClose={() => setShowInstall(false)} />}

      {/* 3.53.0: the guided tour */}
      {tourSteps && <GuidedTour steps={tourSteps} onEnd={endTour} />}

      {/* What's new */}
      {showWhatsNew && <WhatsNew level={isAdmin ? 'super_admin' : (effectiveInfo?.level || 'user')} onClose={() => setShowWhatsNew(false)} />}
    </div>
  );
}

function LoadingScreen({ offline = false }) {
  // 3.40.0: the same soft background as the sign-in screens, the logo in a
  // turning ring (3.40.1: components/Loader, used all over the portal).
  // 3.47.1: in the theme and light or dark last shown in this browser
  useLoadingLook();
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(120deg, var(--c-e0f2f1), var(--b-f1f8e9), var(--c-e3f2fd), var(--c-e0f2f1))', backgroundSize: '300% 300%',
      animation: 'authHue 22s ease infinite', fontFamily: "'DM Sans', 'Segoe UI', sans-serif"
    }}>
      <style>{'@keyframes authHue { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }'}</style>
      <Loader size="lg" label={offline ? "Can't reach the Fax Portal. Check your internet connection. This page will continue by itself." : 'Opening your Fax Portal…'} />
    </div>
  );
}

// Links from the "Reset two-factor authentication" and "Request access"
// emails open their own pages, whether or not someone is signed in
function Root() {
  const [resetToken]  = useState(resetTokenFromLocation);
  const [accessToken] = useState(accessTokenFromLocation);
  const [passwordToken] = useState(setPasswordTokenFromLocation);
  const [confirmToken] = useState(confirmEmailTokenFromLocation);
  const [resetCode] = useState(resetPasswordCodeFromLocation);   // 3.40.1: the "Reset your password" email
  const [faxEmailsLink] = useState(faxEmailsLinkFromLocation);   // 3.59.2: "Stop emailing me each new fax"
  const [sharedNoteLink] = useState(sharedNoteLinkFromLocation);   // 3.77.0: a note shared with an outside address
  if (resetToken)  return <ResetTwoFactorLink token={resetToken} />;
  if (confirmToken) return <ConfirmEmail token={confirmToken} />;
  if (passwordToken) return <SetPassword token={passwordToken} />;
  if (resetCode !== null) return <ResetPassword code={resetCode} />;
  if (accessToken) return <RequestAccess token={accessToken} />;
  if (faxEmailsLink) return <FaxEmails link={faxEmailsLink} />;
  if (sharedNoteLink) return <SharedNote link={sharedNoteLink} />;
  return <AppContent />;
}

// Development only: ?layout-preview shows the portal with a pretend
// Super Admin and empty data (src/dev/LayoutPreview.jsx), for checking the
// layout at different window sizes. Removed from production builds.
// (The NODE_ENV test must sit right on the require so the build drops it.)
const Auth = process.env.NODE_ENV === 'development'
  && new URLSearchParams(window.location.search).has('layout-preview')
  // eslint-disable-next-line global-require
  ? require('./dev/LayoutPreview').FakeAuth : AuthProvider;

export default function App() {
  return (
    <Auth>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
        html { background: var(--c-f4f7f6); }   /* 3.74.5: behind the page (iOS back/forward swipe, overscroll) the portal's colour, never white; 3.74.7 (owner): the swipe itself works again */
        html[data-mode="dark"] body { background: var(--c-f4f7f6); }
        /* 3.47.0 (accessibility): a clear ring on whatever the keyboard is on, in the theme's colour */
        :is(button, a, [role="tab"], [role="radio"], summary, [tabindex]:not([tabindex="-1"])):focus-visible { outline: 2.5px solid var(--c-00897b); outline-offset: 2px; }
        /* 3.47.3: inside the menu bar and other sliding-highlight groups, the ring sits inside the
           rounded item (outside it was cut off by the bar's edge into two straight lines) */
        .pill-group > :focus-visible { outline-offset: -3px !important; border-radius: 999px; }
        main:focus { outline: none; }
        .skip-link { position: fixed; left: 12px; top: -60px; z-index: 3000; padding: 10px 16px; border-radius: 10px; font-weight: 700; text-decoration: none;
          background: var(--c-00695c); color: var(--t-ffffff); box-shadow: 0 6px 18px rgb(var(--c-000000-rgb) / 0.25); transition: top .2s ease; }
        .skip-link:focus { top: 12px; }
        /* Boxes to type in that don't set their own colour: the card's colour, not the browser's grey */
        html[data-mode="dark"] :is(input, select, textarea) { background-color: var(--c-fafafa); color: var(--t-263238); }
        /* 3.45.0: the logo's navy lettering, lifted so it reads on the dark header */
        html[data-mode="dark"] .brand-logo { filter: brightness(1.75) saturate(0.9) drop-shadow(0 0 6px rgb(255 255 255 / 0.12)); }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes livePulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
        @keyframes liveGlow {
          0%,100% { box-shadow: 0 0 0 0 rgb(var(--c-43a047-rgb) / 0), 0 0 6px rgb(var(--c-43a047-rgb) / 0.25); }
          50%     { box-shadow: 0 0 0 4px rgb(var(--c-43a047-rgb) / 0.18), 0 0 16px rgb(var(--c-43a047-rgb) / 0.55); }
        }
        @keyframes faxShine { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
        /* 3.70.0: the fax going out after Send (SendTracker) */
        @keyframes trkRing { 0%,100% { box-shadow: inset 0 0 0 4px var(--c-ffffff), inset 0 0 0 11px var(--c-00897b), 0 0 0 0 rgb(var(--c-00897b-rgb) / 0.35); }
          50% { box-shadow: inset 0 0 0 4px var(--c-ffffff), inset 0 0 0 11px var(--c-00897b), 0 0 0 7px rgb(var(--c-00897b-rgb) / 0); } }
        @keyframes trkBreathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
        @keyframes trkSheet { 0% { opacity: 0; transform: translateY(-3px); } 40%,70% { opacity: 1; transform: none; } 100% { opacity: 0; transform: translateY(3px); } }
        @keyframes trkIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: none; } }
        .trk-ring { animation: trkRing 1.6s ease-in-out infinite; }
        .trk-pop { animation: badgePop .35s ease both; }
        .trk-float { animation: trkBreathe 2.4s ease-in-out infinite; }
        .trk-sheet { animation: trkSheet 1.6s ease-in-out infinite; transform-box: fill-box; }
        .trk-in { animation: trkIn .3s ease both; }
        @keyframes badgePop { 0% { transform: scale(0.6); opacity: 0; } 70% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); } }
        @keyframes rowFlash { 0% { background: var(--c-fff59d); } 100% { background: transparent; } }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
        /* 3.51.0 (phones): boxes to type in at 16px, so iPhone doesn't zoom in on them; no grey flash on tap */
        @media ${PHONE_MEDIA} {
          input:not([type="checkbox"]):not([type="radio"]), select, textarea { font-size: 16px !important; }
          html { -webkit-text-size-adjust: 100%; }
          /* nothing that sticks out (a hidden menu, a wide table) may widen the phone's page */
          html, body { overflow-x: clip; }
          /* 3.52.0: iPhones with a notch or home bar (viewport-fit=cover): nothing under them */
          body { padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); }
          .app-header { padding-top: calc(8px + env(safe-area-inset-top, 0px)) !important; }
          a, button, [role="button"] { -webkit-tap-highlight-color: transparent; }
          /* section buttons (Settings, Users, Logs…): one row you swipe sideways instead of several rows */
          main [role="tablist"] { flex-wrap: nowrap !important; overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: none; padding-bottom: 2px; }
          main [role="tablist"]::-webkit-scrollbar { display: none; }
          main [role="tablist"] > * { flex-shrink: 0; white-space: nowrap; }
        }
        @keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }
        .user-menu-items > * { animation: slideIn .32s ease both; }
        .rail-item:hover { background: var(--c-e0f2f1); }
        /* Overview (3.36.4): cards lift, rows light up, bars and the ring grow in */
        .ov-lift { transition: transform .25s cubic-bezier(.34,1.5,.64,1), box-shadow .25s ease, border-color .25s ease; }
        .ov-lift:hover { transform: scale(1.025); box-shadow: 0 18px 36px -6px rgb(var(--c-00695c-rgb) / 0.26) !important; border-color: var(--b-66bb6a) !important; }
        /* 3.40.2: pointed at, the Overview's top cards fill with softly moving colour */
        .ov-lift { position: relative; isolation: isolate; }
        .ov-lift::before { content: ''; position: absolute; inset: 0; z-index: -1; border-radius: inherit; opacity: 0; transition: opacity .35s ease;
          background: linear-gradient(120deg, var(--c-e0f2f1), var(--b-f1f8e9) 35%, var(--c-e3f2fd) 70%, var(--c-e0f2f1)); background-size: 300% 300%; animation: cpAmbHue 6s ease infinite; }
        .ov-lift:hover::before { opacity: 1; }
        /* 3.48.6: a card grows from its centre instead of sliding up: sliding moved it out from under a
           pointer resting on its bottom edge, so it dropped back, lifted again and shook. Grown, it always
           still covers the spot it sat on, on every edge; the deeper shadow keeps the lifted look */
        /* the Overview's Find a fax box: a moving teal-green-blue border while you type in it */
        input.ov-search:focus { border-color: transparent !important; box-shadow: 0 0 0 4px rgb(var(--c-00897b-rgb) / 0.10) !important;
          background: linear-gradient(var(--c-ffffff), var(--c-ffffff)) padding-box, linear-gradient(90deg, var(--c-00897b), var(--b-43a047), var(--c-1e88e5), var(--c-00897b)) border-box;
          background-size: 100% 100%, 300% 100%; animation: cpSearchRing 3s linear infinite; }
        @keyframes cpSearchRing { from { background-position: 0 0, 0% 0; } to { background-position: 0 0, 150% 0; } }
        main section { transition: box-shadow .25s ease, border-color .25s ease, transform .25s ease; }
        main section:hover { box-shadow: 0 10px 28px rgb(var(--c-00695c-rgb) / 0.13) !important; border-color: var(--b-a5d6a7) !important; }
        .ov-row { transition: background .15s ease; border-radius: 6px; }
        .ov-row:hover { background: var(--c-f0faf8); }
        @keyframes ovGrowY { from { transform: scaleY(0); } to { transform: scaleY(1); } }
        @keyframes ovGrowX { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes ovRingIn { from { transform: rotate(-120deg) scale(.8); opacity: 0; } to { transform: none; opacity: 1; } }
        .ov-grow-y { transform-origin: bottom; animation: ovGrowY .55s cubic-bezier(.2,.8,.2,1) backwards; }
        /* 3.40.2: Cost Analysis bars (Console and the Overview pop-up): grow up in turn, amounts fade in, lift on hover; the forecast bar breathes */
        .cost-bar-stack { transition: transform .25s cubic-bezier(.34,1.5,.64,1), filter .25s ease, box-shadow .25s ease; }
        .cost-bar:hover .cost-bar-stack { transform: translateY(-4px) scaleX(1.08); filter: brightness(1.08) saturate(1.1); box-shadow: 0 8px 18px rgb(var(--c-004d40-rgb) / 0.22); }
        .cost-bar-value { animation: fadeIn .5s ease backwards; transition: transform .25s ease, color .25s ease; }
        .cost-bar:hover .cost-bar-value { transform: translateY(-4px); color: var(--t-00695c); font-weight: 700; }
        .cost-bar-forecast { animation: ovGrowY .55s cubic-bezier(.2,.8,.2,1) backwards, cpForecast 2.4s ease-in-out .8s infinite; }
        @keyframes cpForecast { 0%, 100% { opacity: .55; } 50% { opacity: .8; } }
        .ov-grow-x { transform-origin: left; animation: ovGrowX .7s cubic-bezier(.2,.8,.2,1) backwards; }
        .ov-ring-in { animation: ovRingIn .6s cubic-bezier(.2,.8,.2,1) backwards; }
        ${Array.from({ length: 18 }, (_, i) => `.user-menu-items > *:nth-child(${i + 1}) { animation-delay: ${80 + i * 35}ms; }`).join('\n        ')}
        input:focus, select:focus, textarea:focus { outline: none; border-color: var(--c-00897b) !important; box-shadow: 0 0 0 3px rgb(var(--c-00897b-rgb) / 0.14); }
        button:hover:not(:disabled) { opacity: 0.88; }
        tr:hover td { background: var(--c-f0faf8) !important; }
        /* 3.39.0: the motion layer (all of it switches off with reduced motion, above) */
        html { scroll-behavior: smooth; }
        button, a, input, select, textarea { transition: background-color .18s ease, color .18s ease, border-color .18s ease, box-shadow .18s ease, opacity .18s ease, filter .12s ease; }
        button:active:not(:disabled) { filter: brightness(0.93); }
        td { transition: background-color .15s ease; }
        @keyframes pageIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        .page-in { animation: pageIn .32s cubic-bezier(.2,.8,.2,1) backwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        /* 3.39.1: livelier: pop-ups spring up, the page behind blurs */
        @keyframes modalIn { 0% { opacity: 0; transform: translateY(28px) scale(.92); } 70% { opacity: 1; transform: translateY(-3px) scale(1.01); } 100% { opacity: 1; transform: none; } }
        @keyframes blurIn { from { opacity: 0; backdrop-filter: blur(0); } to { opacity: 1; backdrop-filter: blur(3px); } }
        [role="dialog"][aria-modal="true"] { animation: modalIn .42s cubic-bezier(.2,.8,.2,1) backwards; }
        div:has(> [role="dialog"][aria-modal="true"]) { animation: blurIn .25s ease backwards; backdrop-filter: blur(3px); }
        .nav-seg:not([aria-current]):hover { background: rgb(var(--c-e0f2f1-rgb) / 0.9) !important; color: var(--t-004d40) !important; }
        .nav-seg[aria-current="page"] { animation: navPop .3s cubic-bezier(.2,.8,.2,1); }
        @keyframes navPop { 0% { transform: scale(.94); } 60% { transform: scale(1.03); } 100% { transform: none; } }
        .reveal-pending { opacity: 0; transform: translateY(16px); }
        .reveal-in { opacity: 1; transform: none; transition: opacity .5s ease, transform .5s cubic-bezier(.2,.8,.2,1); }
        .rail-item { transition: background-color .15s ease, transform .15s ease; }
        /* 3.43.5: Sign out under your name: fills red when pointed at */
        .menu-signout { transition: transform .2s cubic-bezier(.34,1.5,.64,1), background-color .2s ease, color .2s ease, box-shadow .2s ease; }
        .menu-signout:hover { background: var(--c-c62828) !important; color: var(--t-ffffff) !important; border-color: var(--c-c62828) !important; transform: scale(1.03); box-shadow: 0 6px 14px rgb(var(--c-c62828-rgb) / 0.25); }
        .rail-item:hover { box-shadow: inset 3px 0 0 var(--c-26a69a); }   /* 3.56.8: no slide (it flickered at the edges) */
        .rail-item-right:hover { box-shadow: inset -3px 0 0 var(--c-26a69a); }   /* 3.48.7: the right strip leans left */
        /* 3.63.0 (owner, Oct 22): the left and right strips take the new sign-in page's look: deep theme colour
           going darker, soft glows, a faint grid. Text is light on it. (The menu under your name stays light.) */
        .cp-ambient-host.cp-night { background: linear-gradient(170deg, color-mix(in srgb, var(--c-004d40) 78%, #000), color-mix(in srgb, var(--c-004d40) 42%, #000) 70%, color-mix(in srgb, var(--c-004d40) 30%, #000)) !important;
          background-size: 100% 100% !important; animation: none; border-color: rgba(255,255,255,.08) !important; color-scheme: dark; }
        .cp-night .cp-ambient::before { content: ''; position: absolute; inset: 0; opacity: .5;
          background-image: linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px); background-size: 30px 30px; }
        .cp-night .cp-amb-blob { opacity: .5; filter: blur(46px); }
        .cp-night .cp-amb-blob.a { background: radial-gradient(circle, rgb(var(--c-26a69a-rgb) / .75), transparent 66%); }
        .cp-night .cp-amb-blob.b { background: radial-gradient(circle, rgb(var(--c-1e88e5-rgb) / .55), transparent 66%); }
        .cp-night .cp-amb-sheet { background: rgba(255,255,255,.09); box-shadow: none; }
        .cp-night .cp-amb-sheet::before, .cp-night .cp-amb-sheet::after { background: rgba(255,255,255,.22); }
        .cp-night .rail-item { color: rgba(255,255,255,.88) !important; }
        .cp-night .rail-item[aria-current="page"] { color: #fff !important; }
        .cp-night .rail-item.warn { color: #FFB74D !important; }
        .cp-night .rail-item:hover:not([aria-current="page"]) { background: rgba(255,255,255,.09); }
        .cp-night .rail-item:hover { box-shadow: inset 3px 0 0 var(--c-80cbc4); }
        .cp-night .rail-item-right:hover { box-shadow: inset -3px 0 0 var(--c-80cbc4); }
        .cp-night .rail-muted { color: rgba(255,255,255,.5) !important; }
        .cp-night .rail-chev { color: var(--c-80cbc4) !important; }
        .cp-night :is(a, button):focus-visible { outline-color: var(--c-80cbc4); }
        /* ── 3.42.0: one modern look everywhere ─────────────────────────────
           Done here, by what things already look like, so every page and pop-up
           follows without its own copy (theme/tabs.js colours stay the source). */
        /* The header: a soft colour that slowly moves; half see-through (frosted) once you scroll */
        .app-header { position: relative; background: linear-gradient(115deg, var(--c-ebf7f4), var(--b-f5fbf1) 28%, var(--c-eef5fc) 55%, var(--c-f3f0fa) 80%, var(--c-ebf7f4)) !important; background-size: 260% 260% !important; animation: cpAmbHue 24s ease infinite; }
        .app-header.is-scrolled, .app-nav-row.is-scrolled::before { background: linear-gradient(115deg, rgb(var(--c-ebf7f4-rgb) / 0.5), rgb(var(--b-f5fbf1-rgb) / 0.5) 28%, rgb(var(--c-eef5fc-rgb) / 0.5) 55%, rgb(var(--c-f3f0fa-rgb) / 0.5) 80%, rgb(var(--c-ebf7f4-rgb) / 0.5)) !important; background-size: 260% 260% !important; background-attachment: fixed !important; }
        /* 3.68.2 (owner): scrolled down, the band behind the menu bar is frosted like the
           header (same glass, one gradient for both, so no seam); same height, nothing moves */
        /* 3.68.4 (owner): only between the two side menus, so they stay on top and never blur */
        .app-nav-row { position: relative; isolation: isolate; }
        .app-nav-row.is-scrolled::before { content: ''; position: absolute; top: 0; bottom: 0; left: var(--rail-w); right: var(--rail-w); z-index: -1; pointer-events: none;
          animation: cpAmbHue 24s ease infinite; backdrop-filter: blur(14px) saturate(1.5); -webkit-backdrop-filter: blur(14px) saturate(1.5); box-shadow: 0 6px 24px rgb(var(--c-00695c-rgb) / 0.14); }
        /* 3.42.2: a soft shine sweeps across the header every few seconds (its own layer, so
           pop-up hints under the header aren't cut off) */
        .app-header-shine { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; }
        .app-header-shine::after { content: ''; position: absolute; top: 0; bottom: 0; width: 28%; left: -40%; transform: skewX(-20deg);
          background: linear-gradient(90deg, transparent, rgb(var(--c-ffffff-rgb) / calc(0.75 * var(--shine-k))), transparent); animation: cpHeadShine 9s ease-in-out 2s infinite; }
        @keyframes cpHeadShine { 0%, 72% { left: -40%; } 100% { left: 120%; } }
        .app-header > :not(.app-header-shine) { position: relative; z-index: 1; }
        /* 3.42.2: icons that move (components/Emo): each has its own motion; it plays while
           you point at what it's in and keeps going on the page you're on */
        .cp-ico { display: inline-block; transform-origin: 50% 60%; will-change: transform; }
        /* 3.55.0: the Flux icons move their own parts (components/fluxIcons.js), so the wrapper stays still */
        .cp-ico.cp-ico-flux { animation: none !important; will-change: auto; line-height: 0; } .cp-ico-flux svg { display: block; }
        .ico-hop { --m: icoHop; } .ico-write { --m: icoWrite; } .ico-send { --m: icoSend; } .ico-drop { --m: icoDrop; } .ico-shake { --m: icoShake; }
        .ico-flip { --m: icoFlip; } .ico-scroll { --m: icoScroll; } .ico-grow { --m: icoGrow; } .ico-nod { --m: icoNod; } .ico-print { --m: icoPrint; }
        .ico-spin { --m: icoSpin; --t: linear; --d: 1.6s; } .ico-tilt { --m: icoTilt; } .ico-flipv { --m: icoFlipV; } .ico-beat { --m: icoBeat; } .ico-coin { --m: icoCoin; }
        .ico-drawer { --m: icoDrawer; } .ico-bob { --m: icoBob; } .ico-twinkle { --m: icoTwinkle; } .ico-search { --m: icoSearch; } .ico-ring { --m: icoRing; }
        .ico-float { --m: icoFloat; } .ico-blink { --m: icoBlink; } .ico-pop { --m: icoPop; } .ico-abacus { --m: icoAbacus; } .ico-type { --m: icoType; }
        :is(a, button, label, summary, section, [role="tab"], [role="button"], [role="option"], .cp-ico-host, .ov-lift):hover .cp-ico,
        :is(a, button, [role="tab"], [role="button"]):focus-visible .cp-ico,
        :is([aria-current="page"], [aria-selected="true"], [aria-pressed="true"]) .cp-ico,
        .cp-title .cp-ico, .dlg-head h2 .cp-ico, .dlg-head strong .cp-ico { animation: var(--m) var(--d, 1.3s) var(--t, ease-in-out) infinite; }
        .cp-title .cp-ico, .cp-ico.cp-ico-once, .dlg-head h2 .cp-ico, .dlg-head strong .cp-ico { animation-iteration-count: 2; }
        .dlg-head:hover h2 .cp-ico, .dlg-head:hover strong .cp-ico { animation-iteration-count: infinite; }
        @keyframes icoHop { 0%, 100% { transform: none; } 30% { transform: translateY(-5px); } 50% { transform: translateY(0) scale(1.1, .9); } 65% { transform: translateY(-2px); } }
        @keyframes icoWrite { 0%, 100% { transform: none; } 20% { transform: rotate(-14deg) translate(-2px, 1px); } 40% { transform: rotate(4deg) translate(2px, 0); } 60% { transform: rotate(-12deg) translate(-1px, 1px); } 80% { transform: rotate(6deg) translate(3px, 0); } }
        @keyframes icoSend { 0% { transform: none; opacity: 1; } 40% { transform: translate(7px, -7px) scale(.75); opacity: 0; } 41% { transform: translate(-7px, 7px) scale(.75); opacity: 0; } 75%, 100% { transform: none; opacity: 1; } }
        @keyframes icoDrop { 0% { transform: translateY(-8px); opacity: 0; } 35% { transform: translateY(1px); opacity: 1; } 50% { transform: translateY(-2px); } 65%, 100% { transform: none; opacity: 1; } }
        @keyframes icoShake { 0%, 100% { transform: none; } 15% { transform: rotate(-13deg); } 30% { transform: rotate(11deg); } 45% { transform: rotate(-8deg); } 60% { transform: rotate(5deg); } 75% { transform: none; } }
        @keyframes icoFlip { 0% { transform: perspective(200px) rotateY(0); } 50% { transform: perspective(200px) rotateY(180deg); } 100% { transform: perspective(200px) rotateY(360deg); } }
        @keyframes icoScroll { 0%, 100% { transform: none; } 30% { transform: translateY(-3px) scaleY(1.08); } 60% { transform: translateY(2px) scaleY(.95); } }
        @keyframes icoGrow { 0%, 100% { transform: none; } 40% { transform: translateY(-3px) scale(1.25); } 70% { transform: scale(.95); } }
        @keyframes icoNod { 0%, 100% { transform: none; } 25% { transform: rotate(-9deg); } 50% { transform: rotate(7deg) translateY(-1px); } 75% { transform: rotate(-4deg); } }
        @keyframes icoPrint { 0%, 100% { transform: none; } 10%, 30%, 50%, 70% { transform: translateX(-1.2px) translateY(.5px); } 20%, 40%, 60%, 80% { transform: translateX(1.2px); } }
        @keyframes icoSpin { to { transform: rotate(360deg); } }
        @keyframes icoTilt { 0%, 100% { transform: none; } 30% { transform: rotate(-12deg) scale(1.08); } 65% { transform: rotate(7deg); } }
        @keyframes icoFlipV { 0%, 40% { transform: none; } 60%, 100% { transform: rotate(180deg); } }
        @keyframes icoBeat { 0%, 60%, 100% { transform: none; } 15% { transform: scale(1.28); } 30% { transform: scale(1); } 45% { transform: scale(1.18); } }
        @keyframes icoCoin { 0%, 100% { transform: none; } 40% { transform: translateY(-6px) perspective(200px) rotateY(180deg); } 70% { transform: translateY(0) perspective(200px) rotateY(360deg); } }
        @keyframes icoDrawer { 0%, 100% { transform: none; } 40% { transform: translateX(4px); } 60% { transform: translateX(-1px); } }
        @keyframes icoBob { 0%, 100% { transform: none; } 50% { transform: translateY(-4px) rotate(-4deg); } }
        @keyframes icoTwinkle { 0%, 100% { transform: none; filter: none; } 50% { transform: scale(1.25) rotate(15deg); filter: brightness(1.3) drop-shadow(0 0 4px rgb(var(--c-ffc107-rgb) / 0.8)); } }
        @keyframes icoSearch { 0%, 100% { transform: none; } 25% { transform: translate(3px, -2px); } 50% { transform: translate(0, -4px); } 75% { transform: translate(-3px, -2px); } }
        @keyframes icoRing { 0%, 50%, 100% { transform: none; } 10% { transform: rotate(16deg); } 20% { transform: rotate(-14deg); } 30% { transform: rotate(10deg); } 40% { transform: rotate(-6deg); } }
        @keyframes icoFloat { 0%, 100% { transform: none; } 50% { transform: translate(3px, -4px); } }
        @keyframes icoBlink { 0%, 40%, 60%, 100% { transform: none; } 50% { transform: scaleY(.15); } }
        @keyframes icoType { 0%, 100% { transform: none; } 15%, 45%, 75% { transform: translateY(1.5px) rotate(-2deg); } 30%, 60%, 90% { transform: translateY(0) rotate(2deg); } }
        @keyframes icoAbacus { 0%, 100% { transform: none; } 20% { transform: translateX(-3px); } 40% { transform: translateX(3px); } 60% { transform: translateX(-2px) translateY(-1px); } 80% { transform: translateX(2px); } }
        @keyframes icoPop { 0%, 100% { transform: none; } 40% { transform: scale(1.25); } 70% { transform: scale(.95); } }
        /* "Fax Portal": the sign-in page's shining gradient letters */
        .brand-title { background: linear-gradient(110deg, var(--t-004d40), var(--t-00897b) 40%, var(--bt-43a047) 60%, var(--t-004d40)); background-size: 250% 100%; -webkit-background-clip: text; background-clip: text; color: transparent !important; animation: authShine 8s ease-in-out infinite; font-weight: 800 !important; letter-spacing: -0.4px; }
        @keyframes authShine { 0%, 100% { background-position: 0% 0; } 50% { background-position: 100% 0; } }
        /* Buttons, by colour: teal (main actions), slate (Refresh, Download), red and orange
           (careful actions) get the sign-in button's gradient, a shine that sweeps across on
           hover, a lift and a press; white ones (Cancel, Reset) light up teal */
        button:not([role="tab"]):not(.pill-group > *):not(.nav-seg):not(.auth-btn):not(.client-btn):not(.user-card):not(.clock-chip):is([style*="background: var(--c-00897b)"], [style*="background: var(--c-00695c)"], [style*="background: var(--c-37474f)"], [style*="background: var(--c-546e7a)"], [style*="background: var(--c-c62828)"], [style*="background: var(--c-d32f2f)"], [style*="background: var(--c-b71c1c)"], [style*="background: var(--c-ef6c00)"], [style*="background: var(--c-e65100)"]) {
          background-image: linear-gradient(110deg, transparent 38%, rgb(var(--c-ffffff-rgb) / 0.38) 50%, transparent 62%), var(--cp-btn) !important;
          background-size: 260% 100%, 100% 100% !important; background-position: 140% 0, 0 0 !important; background-repeat: no-repeat !important;
          border-color: transparent !important; color: var(--t-ffffff) !important; box-shadow: 0 4px 12px var(--cp-btn-glow);
          transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s ease, background-position .65s ease, filter .2s ease !important; }
        button:is([style*="background: var(--c-00897b)"], [style*="background: var(--c-00695c)"]) { --cp-btn: linear-gradient(135deg, var(--c-00695c), var(--c-00796b) 55%, var(--b-2e7d32)); --cp-btn-glow: rgb(var(--c-00897b-rgb) / 0.28); }
        button:is([style*="background: var(--c-37474f)"], [style*="background: var(--c-546e7a)"]) { --cp-btn: linear-gradient(135deg, var(--c-37474f), var(--c-546e7a) 60%, var(--c-607d8b)); --cp-btn-glow: rgb(var(--c-37474f-rgb) / 0.25); }
        button:is([style*="background: var(--c-c62828)"], [style*="background: var(--c-d32f2f)"], [style*="background: var(--c-b71c1c)"]) { --cp-btn: linear-gradient(135deg, var(--c-b71c1c), var(--c-e53935) 60%, var(--c-ef5350)); --cp-btn-glow: rgb(var(--c-c62828-rgb) / 0.28); }
        button:is([style*="background: var(--c-ef6c00)"], [style*="background: var(--c-e65100)"]) { --cp-btn: linear-gradient(135deg, var(--c-a14400), var(--c-bf360c) 60%, var(--c-c43e00)); --cp-btn-glow: rgb(var(--c-ef6c00-rgb) / 0.28); }
        button:not([role="tab"]):not(.pill-group > *):not(.nav-seg):not(:disabled):is([style*="background: var(--c-00897b)"], [style*="background: var(--c-00695c)"], [style*="background: var(--c-37474f)"], [style*="background: var(--c-546e7a)"], [style*="background: var(--c-c62828)"], [style*="background: var(--c-d32f2f)"], [style*="background: var(--c-b71c1c)"], [style*="background: var(--c-ef6c00)"], [style*="background: var(--c-e65100)"]):hover {
          background-position: -40% 0, 0 0 !important; transform: scale(1.03); box-shadow: 0 10px 22px var(--cp-btn-glow); opacity: 1 !important; filter: saturate(1.1); }
        button:not(:disabled):is([style*="background: var(--c-00897b)"], [style*="background: var(--c-00695c)"], [style*="background: var(--c-37474f)"], [style*="background: var(--c-546e7a)"], [style*="background: var(--c-c62828)"], [style*="background: var(--c-d32f2f)"], [style*="background: var(--c-b71c1c)"], [style*="background: var(--c-ef6c00)"], [style*="background: var(--c-e65100)"]):active { filter: brightness(.94); }
        main button:not([role="tab"]):not(.pill-group > *):not(:disabled)[style*="background: var(--c-ffffff)"][style*="border: 1"]:hover,
        [role="dialog"] button:not([role="tab"]):not(.pill-group > *):not(:disabled)[style*="background: var(--c-ffffff)"][style*="border: 1"]:hover {
          background: linear-gradient(135deg, var(--c-f0faf8), var(--b-e8f5e9)) !important; border-color: var(--c-4db6ac) !important; color: var(--t-00695c) !important; transform: scale(1.03); box-shadow: 0 8px 18px rgb(var(--c-00695c-rgb) / 0.14); opacity: 1 !important; }
        /* 3.42.1: a small toggle that's on (★ Priority, Block on the Overview) glows amber */
        button[aria-pressed="true"][style*="background: var(--c-fff8e1)"] { background: linear-gradient(110deg, transparent 38%, rgb(var(--c-ffffff-rgb) / calc(0.6 * var(--shine-k))) 50%, transparent 62%), linear-gradient(135deg, var(--c-fff8e1), var(--c-ffe0b2)) !important;
          background-size: 260% 100%, 100% 100% !important; background-position: 140% 0, 0 0 !important; background-repeat: no-repeat !important; border-color: var(--c-ffb74d) !important; color: var(--t-e65100) !important;
          box-shadow: 0 3px 10px rgb(var(--c-ef6c00-rgb) / 0.18); transition: transform .2s cubic-bezier(.34,1.5,.64,1), box-shadow .2s, background-position .65s ease !important; }
        button[aria-pressed="true"][style*="background: var(--c-fff8e1)"]:hover:not(:disabled) { background-position: -40% 0, 0 0 !important; transform: scale(1.04); box-shadow: 0 8px 18px rgb(var(--c-ef6c00-rgb) / 0.25); }
        /* Fields with a border: a clear teal-grey border at rest; while typing, a moving
           teal-green-blue border (as on the Overview's Find a fax). Tick boxes, fields in
           dark table headers and fields marked wrong keep their own look. */
        :is(input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="color"]), select, textarea):not(th *):not(.auth-root *):not([aria-invalid="true"])[style*="border: 1"] { border-color: var(--field) !important; }
        :is(input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="color"]), select, textarea):not(th *):not(.auth-root *):not([aria-invalid="true"])[style*="border: 1"]:hover { border-color: var(--field-hover) !important; }
        :is(input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="color"]), select, textarea):not(th *):not(.auth-root *):not([aria-invalid="true"])[style*="border: 1"]:focus {
          border-color: transparent !important; box-shadow: 0 0 0 4px rgb(var(--c-00897b-rgb) / 0.10) !important;
          background: linear-gradient(var(--c-ffffff), var(--c-ffffff)) padding-box, linear-gradient(90deg, var(--c-00897b), var(--b-43a047), var(--c-1e88e5), var(--c-00897b)) border-box !important;
          background-size: 100% 100%, 300% 100% !important; animation: cpSearchRing 3s linear infinite; }
        input.ov-search { border-color: var(--field) !important; }
        /* 3.42.1: fields made of a box with chips and a borderless box inside (Compose receivers,
           Outbox Sent by / Sent to): the outer box gets the same border and moving line */
        .cp-field-box { border-color: var(--field) !important; transition: border-color .2s ease, box-shadow .2s ease; }
        .cp-field-box:hover { border-color: var(--field-hover) !important; }
        .cp-field-box:focus-within { border-color: transparent !important; box-shadow: 0 0 0 4px rgb(var(--c-00897b-rgb) / 0.10) !important;
          background: linear-gradient(var(--c-ffffff), var(--c-ffffff)) padding-box, linear-gradient(90deg, var(--c-00897b), var(--b-43a047), var(--c-1e88e5), var(--c-00897b)) border-box !important;
          background-size: 100% 100%, 300% 100% !important; animation: cpSearchRing 3s linear infinite; }
        .cp-field-box input:focus { box-shadow: none !important; border-color: transparent !important; background: transparent !important; animation: none !important; }
        /* 3.42.1: the HIPAA badge: a shield that softly glows; pointed at, it lifts, shines and the shield bounces */
        .phi-badge { position: relative; overflow: hidden; background: linear-gradient(120deg, var(--c-e0f2f1), var(--b-f1f8e9) 50%, var(--c-e3f2fd)) !important; background-size: 220% 220% !important;
          animation: cpAmbHue 9s ease infinite; transition: transform .25s cubic-bezier(.34,1.5,.64,1), box-shadow .25s ease, border-color .25s ease; }
        .phi-badge .phi-shield { display: inline-block; animation: cpShieldGlow 2.8s ease-in-out infinite; }
        @keyframes cpShieldGlow { 0%, 100% { filter: drop-shadow(0 0 0 rgb(var(--c-00897b-rgb) / 0)); transform: none; } 50% { filter: drop-shadow(0 0 5px rgb(var(--c-00897b-rgb) / 0.55)); transform: scale(1.08); } }
        .phi-badge::after { content: ''; position: absolute; top: 0; bottom: 0; width: 40%; left: -60%; transform: skewX(-20deg); pointer-events: none;
          background: linear-gradient(90deg, transparent, rgb(var(--c-ffffff-rgb) / calc(0.8 * var(--shine-k))), transparent); transition: left .7s ease; }
        :hover > .phi-badge, :focus > .phi-badge { transform: scale(1.05); box-shadow: 0 8px 18px rgb(var(--c-00695c-rgb) / 0.18); border-color: var(--c-4db6ac) !important; }
        :hover > .phi-badge::after, :focus > .phi-badge::after { left: 130%; }
        :hover > .phi-badge .phi-shield { animation: cpShieldBounce .6s ease; }
        @keyframes cpShieldBounce { 0%, 100% { transform: none; } 30% { transform: translateY(-3px) rotate(-10deg) scale(1.15); } 60% { transform: translateY(0) rotate(8deg) scale(1.1); } }
        /* Table headers (teal and slate ones): two colours running across the row, a light
           line along the bottom */
        thead tr:has(> th[style*="background: var(--c-00695c)"]), tr[style*="background: var(--c-00695c)"] { background: linear-gradient(90deg, var(--c-004d40), var(--c-00796b) 35%, var(--c-00897b) 60%, var(--b-2e7d32)) !important; box-shadow: inset 0 -2px 0 rgb(var(--b-a5d6a7-rgb) / 0.7); }
        thead tr:has(> th[style*="background: var(--c-546e7a)"]), tr[style*="background: var(--c-546e7a)"] { background: linear-gradient(90deg, var(--c-37474f), var(--c-546e7a) 45%, var(--c-00796b)) !important; box-shadow: inset 0 -2px 0 rgb(var(--c-80cbc4-rgb) / 0.6); }
        thead tr > th:is([style*="background: var(--c-00695c)"], [style*="background: var(--c-546e7a)"]) { background: transparent !important; }
        /* Pop-up titles: a soft moving teal-green-blue band with a slow shine (as on your name card) */
        .dlg-head { position: relative; overflow: hidden; flex-shrink: 0; border-top-left-radius: inherit; border-top-right-radius: inherit; background: linear-gradient(120deg, var(--c-e0f2f1), var(--b-f1f8e9) 40%, var(--c-e3f2fd) 75%, var(--c-e0f2f1)) !important; background-size: 300% 300% !important; animation: cpAmbHue 14s ease infinite; }
        .dlg-head::after { content: ''; position: absolute; top: 0; bottom: 0; width: 35%; left: -50%; transform: skewX(-20deg); pointer-events: none;
          background: linear-gradient(90deg, transparent, rgb(var(--c-ffffff-rgb) / calc(0.7 * var(--shine-k))), transparent); animation: cpDlgShine 7s ease-in-out 1s infinite; }
        @keyframes cpDlgShine { 0%, 70% { left: -50%; } 100% { left: 130%; } }
        /* 3.40.2: soft moving background for the icon strip and the menu under your name (components/Ambient) */
        .cp-ambient-host { background: linear-gradient(160deg, var(--c-f3fbf9), var(--b-eef8f0) 45%, var(--c-eef5fb) 100%) !important; background-size: 200% 200% !important; animation: cpAmbHue 18s ease infinite; }
        .cp-ambient-host > :not(.cp-ambient) { position: relative; z-index: 1; }
        .cp-ambient { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 0; border-radius: inherit; }
        @keyframes cpAmbHue { 0%, 100% { background-position: 0% 0%; } 50% { background-position: 100% 100%; } }
        .cp-amb-blob { position: absolute; border-radius: 50%; filter: blur(40px); opacity: .45; }
        .cp-amb-blob.a { width: 220px; height: 220px; left: -90px; top: 18%; background: radial-gradient(circle, var(--c-80cbc4), transparent 70%); animation: cpAmbFloat 16s ease-in-out infinite; }
        .cp-amb-blob.b { width: 200px; height: 200px; right: -80px; bottom: 6%; background: radial-gradient(circle, var(--b-a5d6a7), transparent 70%); animation: cpAmbFloat 20s ease-in-out infinite reverse; }
        @keyframes cpAmbFloat { 0%, 100% { translate: 0 0; } 50% { translate: 30px -40px; } }
        .cp-amb-sheet { position: absolute; bottom: -40px; width: 18px; height: 24px; border-radius: 3px; background: rgb(var(--c-ffffff-rgb) / calc(0.85 * var(--sheet-k)));
          box-shadow: 0 3px 8px rgb(var(--c-00695c-rgb) / 0.12); opacity: 0; animation: cpAmbSheet 20s linear infinite; }
        .cp-amb-sheet::before, .cp-amb-sheet::after { content: ''; position: absolute; left: 4px; right: 4px; height: 1.5px; border-radius: 1px; background: rgb(var(--c-00897b-rgb) / 0.35); }
        .cp-amb-sheet::before { top: 6px; } .cp-amb-sheet::after { top: 10px; right: 7px; }
        .cp-amb-sheet.s0 { left: 22%; animation-delay: 0s; } .cp-amb-sheet.s1 { left: 62%; animation-delay: 7s; transform: scale(.8); }
        .cp-amb-sheet.s2 { left: 40%; animation-delay: 13s; transform: scale(.9); } .cp-amb-sheet.s3 { left: 80%; animation-delay: 17s; transform: scale(.75); }
        @keyframes cpAmbSheet { 0% { translate: 0 0; rotate: -8deg; opacity: 0; } 10% { opacity: .75; } 85% { opacity: .75; } 100% { translate: 18px -105vh; rotate: 12deg; opacity: 0; } }
        /* 3.40.2: your card at the top right: light teal that slowly shifts; lifts, shines and waves when pointed at */
        .user-card { background: linear-gradient(120deg, var(--c-f0faf8), var(--b-e8f5e9) 45%, var(--c-e3f2fd) 100%); background-size: 220% 220%; animation: cpAmbHue 10s ease infinite;
          transition: transform .25s cubic-bezier(.34,1.5,.64,1), box-shadow .25s ease, border-color .25s ease; }
        .user-card::after { content: ''; position: absolute; top: 0; bottom: 0; width: 40%; left: -60%; transform: skewX(-20deg);
          background: linear-gradient(90deg, transparent, rgb(var(--c-ffffff-rgb) / calc(0.75 * var(--shine-k))), transparent); transition: left .7s ease; pointer-events: none; }
        .user-card:hover { transform: scale(1.02); box-shadow: 0 10px 22px rgb(var(--c-00695c-rgb) / 0.18); border-color: var(--c-4db6ac) !important; opacity: 1 !important; }
        .user-card:hover::after { left: 130%; }
        .user-card:hover > :first-child { animation: cpWave .6s ease; }
        @keyframes cpWave { 0%, 100% { transform: none; } 30% { transform: rotate(-10deg) scale(1.08); } 60% { transform: rotate(8deg) scale(1.08); } }
        /* the clock under it: its own soft lavender-blue (not the card's teal); lifts and the clock face ticks round when pointed at */
        .clock-chip { background: linear-gradient(120deg, var(--c-ede7f6), var(--c-e3f2fd)) !important; border-color: var(--c-c5cae9) !important; color: var(--t-3949ab) !important;
          transition: transform .25s cubic-bezier(.34,1.5,.64,1), box-shadow .25s ease; }
        .clock-chip:hover { transform: scale(1.04); box-shadow: 0 8px 18px rgb(var(--c-3949ab-rgb) / 0.18); opacity: 1 !important; }
        .clock-chip .clock-ico { display: inline-block; }
        .clock-chip:hover .clock-ico { animation: cpTick 1s steps(8) infinite; }
        @keyframes cpTick { to { transform: rotate(360deg); } }
        /* 3.39.2: tabs and filter pills inside pages: the highlight slides to
           the one you pick (hooks/useSlidingPills), the others light up on hover */
        .pill-group > [aria-selected="true"], .pill-group > [aria-pressed="true"] { background: transparent !important; border-color: transparent !important; box-shadow: none !important; color: var(--t-ffffff) !important; position: relative; z-index: 2; }
        .pill-ind { position: absolute; z-index: 1; pointer-events: none; background: linear-gradient(135deg, var(--c-00897b), var(--c-26a69a) 55%, var(--b-43a047));
          box-shadow: 0 4px 12px rgb(var(--c-00897b-rgb) / 0.35), inset 0 1px 0 rgb(var(--c-ffffff-rgb) / 0.25);
          transition: left .38s cubic-bezier(.34,1.4,.64,1), width .38s cubic-bezier(.34,1.4,.64,1), top .3s ease, height .3s ease, opacity .2s ease; }
        .pill-group > button:not([aria-selected="true"]):not([aria-pressed="true"]):not(:disabled):hover,
        .pill-group > a[role="tab"]:not([aria-selected="true"]):hover { background: var(--c-e0f2f1) !important; border-color: var(--c-80cbc4) !important; color: var(--t-00695c) !important; transform: scale(1.04); opacity: 1; }
        /* Buttons in the pages grow a little when you point at them and darken when clicked.
           3.56.8 (owner): never move or shrink on hover/press: a button that slid up
           or shrank left the pointer at its edge, lost the hover and came back, over
           and over (flicker on every page); growing keeps the pointer inside it */
        main button { transition: background-color .18s ease, color .18s ease, border-color .18s ease, box-shadow .18s ease, opacity .18s ease, filter .12s ease, transform .18s cubic-bezier(.34,1.5,.64,1); }
        main button:hover:not(:disabled) { transform: scale(1.03); box-shadow: 0 4px 12px rgb(var(--c-00695c-rgb) / 0.16); opacity: 1; }
        main button:active:not(:disabled) { filter: brightness(.94); box-shadow: none; }
      `}</style>
      <NotifyProvider>
        <PreferencesProvider>
          {/* 3.62.2: a page that fails to draw shows a message instead of a blank portal */}
          <ErrorBoundary>
            <Root />
          </ErrorBoundary>
          <UpdateBar />
        </PreferencesProvider>
      </NotifyProvider>
    </Auth>
  );
}

// A user-menu entry; hovering it (or moving to it with the keyboard) shows
// what's inside, e.g. that the password is under Settings (3.9.2). 3.18.0:
// as a small pop-up beside the menu (below the entry on narrow screens), so
// the menu itself doesn't grow.
function MenuItem({ onClick, hint, style, children }) {
  const [show, setShow] = useState(false);
  const narrow = typeof window !== 'undefined' && window.innerWidth < 640;
  return (
    <button onClick={onClick} style={{ ...S.menuItem, ...(show ? S.menuItemHover : {}), ...style }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} onFocus={() => setShow(true)} onBlur={() => setShow(false)}>
      {/* 3.42.2: the item's icon moves when pointed at (components/Emo) */}
      {(() => { const k = Children.toArray(children); if (typeof k[0] === 'string') k[0] = <Fragment key="emo">{emoLabel(k[0])}</Fragment>; return k; })()}
      {/* Always there for screen readers; shown on hover or focus */}
      <span role="tooltip" style={show ? (narrow ? { ...S.menuTip, ...S.menuTipBelow } : S.menuTip) : S.srOnly}>{hint}</span>
    </button>
  );
}

const S = {
  page: {
    minHeight: '100vh', background: 'var(--c-f4f7f6)', fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    display: 'flex', flexDirection: 'column'
  },
  header: {
    background: 'var(--c-ffffff)', borderBottom: '2px solid var(--c-e0f2f1)', padding: '14px 32px', transition: 'padding .2s', pointerEvents: 'auto',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 10,
    boxShadow: '0 2px 8px rgb(var(--c-00695c-rgb) / 0.06)'
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  headerLogo: { height: 40, transition: 'height .2s' },
  portalTitle: { fontSize: 18, fontWeight: 700, color: 'var(--t-00695c)' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8 },
  versionChip: {
    position: 'relative', padding: '2px 8px', borderRadius: 20, border: '1px solid var(--c-b2dfdb)',
    background: 'var(--c-f0faf8)', color: 'var(--t-00695c)', fontSize: 11, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit', letterSpacing: '0.3px'
  },
  newDot: {
    position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%',
    background: 'var(--c-e65100)', border: '2px solid var(--c-ffffff)'
  },
  menuVersion: { fontSize: 11, color: 'var(--t-90a4ae)', marginLeft: 6 },
  menuItemHover: { background: 'rgb(var(--glass-rgb) / 0.75)' },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
  menuTip: {
    position: 'absolute', right: 'calc(100% + 10px)', top: '50%', transform: 'translateY(-50%)', width: 230,
    padding: '7px 10px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', borderRadius: 6, boxShadow: '0 4px 14px rgb(var(--c-000000-rgb) / 0.18)',
    fontSize: 12, lineHeight: 1.4, fontWeight: 400, textAlign: 'left', pointerEvents: 'none', zIndex: 310, whiteSpace: 'normal',
  },
  menuTipBelow: { right: 12, top: 'calc(100% - 4px)', transform: 'none', width: 'auto', left: 12 },
  // The header and the View as bar stay at the top together
  stickyTop: { position: 'sticky', top: 0, zIndex: 100, transition: 'background .25s, box-shadow .25s' },
  // 3.39.0: scrolled down, the fixed bar is see-through (frosted); 3.56.7: same height
  fixedTop: { position: 'fixed', left: 0, right: 0, pointerEvents: 'none' },   // 3.39.2: only the header and the menu's buttons take clicks
  headerGrid: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' },
  // 3.56.7 (owner): one height always; scrolled down only the background turns
  // see-through (frosted), so nothing under the bar moves
  headerScrolled: { borderBottom: '2px solid transparent', background: 'rgb(var(--glass-rgb) / var(--sticky-a))', backdropFilter: 'blur(14px) saturate(1.5)', WebkitBackdropFilter: 'blur(14px) saturate(1.5)', boxShadow: '0 6px 24px rgb(var(--c-00695c-rgb) / 0.14)' },
  // 3.39.2: no band: the menu floats over the page (only its buttons take
  // clicks; the rest of the row lets them through to the page)
  // 3.49.13: clear of the side menus, which now run up beside it
  // minmax(0, 1fr): the client switcher widening on hover never squeezes the menu
  // 3.56.2 (owner): the bell's column is never narrower than what keeps the bell clear of
  // the right side menu when it slides open; on a narrow screen the menu moves left a little
  navRow: { padding: `12px ${RAIL_WIDTH + 24}px 12px`, display: 'grid', gridTemplateColumns: `minmax(0, 1fr) auto minmax(${RIGHT_MENU_OPEN - (RAIL_WIDTH + 24) + 46 + 8}px, 1fr)`, alignItems: 'center', gap: 12,
    background: 'transparent', pointerEvents: 'none', transition: 'padding .2s' },
  viewAsBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 14, flexWrap: 'wrap', padding: '9px 20px', background: 'var(--c-fff3e0)', color: 'var(--t-bf360c)', pointerEvents: 'auto',
    borderBottom: '2px solid var(--c-ffb74d)', fontSize: 14
  },
  viewAsExit: {
    padding: '6px 14px', background: 'var(--c-e65100)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8,
    cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit'
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 },
  headerPhone: { padding: '8px 12px', flexWrap: 'nowrap', gap: 10 },
  headerLeftPhone: { flex: 1, minWidth: 0, gap: 8, overflow: 'hidden' },
  headerRightPhone: { flexWrap: 'nowrap', gap: 8, flexShrink: 0 },
  userBtnPhone: { display: 'flex', padding: 2, border: '2px solid var(--c-b2dfdb)', borderRadius: '50%', background: 'none', cursor: 'pointer', flexShrink: 0 },
  mainPhone: { padding: '14px 12px', overflowX: 'clip' },   // 3.51.0: something too wide never widens the phone's page
  // 3.40.2: a light teal card that slowly shifts colour; it lifts and shines when pointed at
  userBtn: {
    display: 'flex', alignItems: 'center', gap: 8, position: 'relative', overflow: 'hidden',
    border: '1.5px solid var(--c-b2dfdb)', borderRadius: 12, padding: '6px 14px',
    cursor: 'pointer', fontFamily: 'inherit'
  },
  userEmail : { fontSize: 11, color: 'var(--t-90a4ae)' },
  userName  : { fontSize: 13, color: 'var(--t-546e7a)', fontWeight: 600 },
  userRole  : { fontSize: 11, color: 'var(--t-00897b)', fontWeight: 600 },
  adminBadge: {
    background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)', fontSize: 11, fontWeight: 700,
    padding: '2px 8px', borderRadius: 20
  },
  // 3.36.4: a panel down the right side, sliding in and out
  userMenu: {
    position: 'fixed', top: 0, right: 0, bottom: 0, width: 320, maxWidth: 'calc(100vw - 48px)',
    background: 'var(--c-ffffff)', boxShadow: '-10px 0 40px rgb(var(--c-000000-rgb) / 0.16)', borderLeft: '1px solid var(--b-c8e6c9)',
    zIndex: 1000, overflow: 'visible',   // pop-up hints sit outside it, to its left
    transform: 'translateX(105%)', visibility: 'hidden',
    transition: 'transform .3s cubic-bezier(.2,.8,.2,1), visibility 0s linear .3s',
  },
  userMenuOn: { transform: 'none', visibility: 'visible', transition: 'transform .3s cubic-bezier(.2,.8,.2,1), visibility 0s' },
  // 3.48.7: computers: a small card under your name that slides in from the right
  userCard: {
    position: 'fixed', width: 340, maxWidth: 'calc(100vw - 32px)', borderRadius: 14, overflow: 'visible',   // the › close button sits on its left edge
    background: 'var(--c-ffffff)', boxShadow: '0 16px 40px rgb(var(--c-000000-rgb) / 0.18)', border: '1px solid var(--b-c8e6c9)',
    zIndex: 1000, visibility: 'hidden',
    transition: 'transform .3s cubic-bezier(.2,.8,.2,1), visibility 0s linear .3s',
  },
  userCardOn: { transform: 'none', visibility: 'visible', transition: 'transform .3s cubic-bezier(.2,.8,.2,1), visibility 0s' },
  // 3.48.9: the card's insides, a size smaller to fit the name card's width
  cardHeader: { padding: '10px 12px 10px 20px', borderBottom: 'none', borderRadius: 'inherit' },
  cardName: { fontSize: 14 },
  cardLine: { fontSize: 11.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  cardSignOut: { marginTop: 7, padding: '3px 10px', fontSize: 12 },
  cardAvatarEdit: { width: 18, height: 18, fontSize: 10, right: -4, bottom: -4 },
  cardCloseBtn: { left: -14, top: 12, width: 28, height: 28, fontSize: 17, lineHeight: '24px' },
  menuBackdrop: { position: 'fixed', inset: 0, zIndex: 999, background: 'rgb(var(--c-0f2823-rgb) / 0.18)', opacity: 0, pointerEvents: 'none', transition: 'opacity .3s' },
  menuBackdropOn: { opacity: 1, pointerEvents: 'auto' },
  menuCloseBtn: {
    position: 'absolute', left: -18, top: 22, width: 36, height: 36, borderRadius: '50%', border: '1.5px solid var(--b-c8e6c9)',
    background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontSize: 22, fontWeight: 700, lineHeight: '30px', cursor: 'pointer',
    boxShadow: '-2px 2px 8px rgb(var(--c-000000-rgb) / 0.12)', fontFamily: 'inherit', padding: 0, zIndex: 1,
  },
  userMenuHeader: { padding: '18px 18px 14px 28px', borderBottom: '1px solid rgb(var(--c-00695c-rgb) / 0.08)', background: 'rgb(var(--glass-rgb) / 0.55)' },
  userMenuName  : { fontSize: 14, color: 'var(--t-333333)', fontWeight: 700 },
  userMenuEmail : { fontSize: 12, color: 'var(--t-78909c)', marginTop: 2 },
  userMenuRole: { fontSize: 12, color: 'var(--t-00897b)', marginTop: 2 },
  menuSignOut: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '5px 12px', borderRadius: 16, border: '1.5px solid var(--c-ef9a9a)',
    background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },   // 3.43.5
  // 3.37.0: a small heading over each group in the menu
  // 3.37.1: bolder and bigger, so the groups stand out
  // 3.47.3: the theme dots and light · auto · dark at the left of the header (computers)
  headerLook: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' },
  // 3.47.2: Theme on one line: a small heading, the theme dots and light · auto · dark (phones, 3.47.3)
  menuThemeRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 18px', borderTop: '1px solid rgb(var(--c-00695c-rgb) / 0.10)', background: 'rgb(var(--c-e0f2f1-rgb) / 0.55)' },
  menuThemeLabel: { fontSize: 11, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--t-00695c)', marginRight: 2 },
  menuSection: { padding: '10px 18px 5px', fontSize: 13, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--t-00695c)', borderTop: '1px solid rgb(var(--c-00695c-rgb) / 0.10)', background: 'rgb(var(--c-e0f2f1-rgb) / 0.55)' },
  menuCount: { display: 'inline-block', minWidth: 18, padding: '1px 6px', marginLeft: 8, borderRadius: 10, background: 'var(--c-bf360c)', color: 'var(--t-ffffff)', fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center' },
  menuItem: {
    display: 'block', width: '100%', padding: '9px 18px', background: 'none',
    border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--t-333333)',
    textAlign: 'left', fontFamily: 'inherit', borderBottom: '1px solid rgb(var(--c-00695c-rgb) / 0.06)', position: 'relative'
  },
  main: { maxWidth: 1400, width: '100%', margin: '0 auto', padding: '28px 24px', flex: 1 },
  userCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },   // the clock centred under the card (3.22.4)
  menuAvatarBtn: { position: 'relative', padding: 0, border: 'none', background: 'none', cursor: 'pointer', borderRadius: '50%', flexShrink: 0 },
  menuAvatarEdit: { position: 'absolute', right: -3, bottom: -3, width: 22, height: 22, borderRadius: '50%', background: 'var(--c-ffffff)', border: '1.5px solid var(--c-b2dfdb)', color: 'var(--t-00695c)', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navCount: { display: 'inline-block', minWidth: 18, padding: '1px 6px', marginLeft: 6, borderRadius: 10, background: 'var(--c-d32f2f)', color: 'var(--t-ffffff)', fontSize: 11, fontWeight: 700, lineHeight: '16px', textAlign: 'center' },
  navDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--c-ef6c00)', marginLeft: 6, verticalAlign: 'middle' },
  content: {}
};